// Program templates. Each program defines workout templates and a FIXED 7-day default rotation.
// The default rotation is rotated based on the user's chosen startDate (Day 1 of their program).
// daysPerWeek is the user's commitment intent — the default schedule is decoupled from it,
// but daysPerWeek drives the weekly-goal stat and may be referenced by special variants.

import { exercises, isBaselineExercise } from './exercises';

// Tags that must NEVER be auto-scheduled into a normal strength workout for anyone:
// - stretching: static stretches mid-workout blunt strength / raise injury risk —
//   they live on Stretch day, as a flag/pacing substitute, or a Log-time cooldown.
// - rehab / adaptive / iron-limb / prosthesis-off / phantom-limb: clinical / amputee
//   work, gated to the relevant submode + flags. NEVER a general user's session.
// - breath: the breath drills are the blank-day fallback, not working sets.
const BASE_EXCLUDE_TAGS = new Set([
  'stretching', 'rehab', 'adaptive', 'iron-limb',
  'prosthesis-off', 'phantom-limb', 'phantom', 'breath',
  // 'cardio' gates pure endurance/conditioning exercises (Rower Intervals, Assault Bike,
  // Battle Rope Waves, etc.) out of ALL strength day slots. These exercises already carry
  // the tag in exercises.js — this just enforces the exclusion. Distinguished from
  // 'conditioning' (which also applies to strength-compatible work like Sled Push, Lateral
  // Shuffle, and combatives conditioning) — 'cardio' is reserved for exercises whose
  // PRIMARY purpose is cardiovascular output, not muscle-building.
  'cardio',
]);
// Combatives is handled SEPARATELY from BASE_EXCLUDE_TAGS (founder spec Jul 2026,
// refined Jul 22 2026 evening — see WORKOUT_CONSTRUCTION_SPEC.md §9.7):
//   • Combatives-SPECIFIC moves (an explicit martial-art TAG — Shadow Boxing Rounds
//     'boxing', Sprawl Drills 'wrestling', Hip Escape 'bjj', Ukemi 'judo', Neck Bridge
//     'wrestling') are a skill/technique that needs coaching/demonstration → scheduled
//     ONLY for users who train combatives.
//   • Everything else carrying a `combatives: true` NOTE (Power Clean, KB Swing, and —
//     as of Jul 22 2026 — Mountain Climbers, Bear Crawl, Diamond Push-Up, Lateral
//     Shuffle, Pogo Jumps) is a general strength/conditioning move that ALSO benefits
//     fighters → eligible for EVERYONE, and PRIORITIZED for combatives users so their
//     work aligns with what benefits their art.
// Gating is TAG-ONLY (fixed Jul 22 2026 evening): the previous version additionally
// gated any BODYWEIGHT move (`equipment: []`) carrying the `combatives: true` note,
// on the theory that a no-equipment combat drill "needs coaching." That heuristic was
// too blunt — it swept up universal bodyweight conditioning moves (Mountain Climbers,
// Bear Crawl) that carry the note purely because they're *useful* for combat athletes,
// not because they're combat-specific technique. Fix: gating is now driven entirely by
// an explicit tag, and every move that genuinely needs coaching (Hip Escape, Shadow
// Boxing Rounds, Sprawl Drills, Ukemi, Neck Bridge) was given one so it stays correctly
// gated on purpose, not by accident of having no equipment.
const COMBATIVES_TAGS = ['combatives', 'bjj', 'mma', 'boxing', 'muay-thai', 'judo', 'wrestling', 'grappling'];
const hasCombativesTag = (e) => Array.isArray(e.tags) && e.tags.some((t) => COMBATIVES_TAGS.includes(t));
const isCombativesSpecific = (e) => hasCombativesTag(e);
const isCombativesBeneficial = (e) => e.combatives === true || hasCombativesTag(e);
const hasExcludedTag = (e) => Array.isArray(e.tags) && e.tags.some((t) => BASE_EXCLUDE_TAGS.has(t));
// Experience is a FLOOR, not an allowlist (fixed Jul 22 2026 late evening — founder
// correction). The exact-match bug: `e.levels.includes(experience)` required an
// exercise's `levels` array to literally contain the user's own level, so an
// exercise tagged e.g. `['beginner', 'intermediate']` (no explicit 'advanced') was
// being EXCLUDED from advanced users — backwards. Founder's model: `levels` marks
// the LOWEST level a move is appropriate for; higher-experience users can always
// do everything at or below their level (an advanced lifter is not blocked from a
// beginner move — they just aren't handed something ABOVE their level). Fix: take
// the lowest level present in `e.levels` and compare the user's level against that
// floor, instead of requiring an exact array membership match.
const LEVEL_ORDER = { beginner: 0, intermediate: 1, advanced: 2 };
const meetsExperienceFloor = (e, experience) => {
  if (!experience) return true; // null/undefined experience (module-load previews) skips all checks
  // Floor: user must be at or above the exercise's minimum level requirement.
  if (Array.isArray(e.levels) && e.levels.length > 0) {
    const floor = Math.min(...e.levels.map((l) => LEVEL_ORDER[l] ?? 0));
    if ((LEVEL_ORDER[experience] ?? 0) < floor) return false;
  }
  // Ceiling: exercises tagged maxLevel:'beginner' (e.g. Banded Pull-Up, Knee Push-Up)
  // are progression tools — appropriate for beginners in scheduled sets but should not
  // appear as working sets for intermediate/advanced lifters who have surpassed them.
  // Users at or below maxLevel: eligible. Users above: ineligible for auto-scheduling
  // (still available in the library and for manual swap — specialist flag not used here
  // because beginners DO need them auto-scheduled).
  if (e.maxLevel) {
    const ceiling = LEVEL_ORDER[e.maxLevel] ?? 2;
    if ((LEVEL_ORDER[experience] ?? 0) > ceiling) return false;
  }
  return true;
};
// Eligibility is user-aware: combatives-specific moves pass only for combatives users,
// and (founder spec Jul 2026 — this "used to exist" per the levels label on every
// exercise, and must again) an exercise is only eligible at or above its own floor
// experience level. A beginner never gets handed an advanced-only movement (e.g.
// Power Clean); an advanced user gets everything at or below their level, including
// beginner-tagged moves (see `meetsExperienceFloor` above). No `experience` (null/''
// — static module-load builds, previews) skips the check entirely.
const makeEligible = (trainsCombatives, experience = null) => (e) =>
  !e.ptClinical && !hasExcludedTag(e) && (trainsCombatives || !isCombativesSpecific(e))
  && meetsExperienceFloor(e, experience);

// djb2 string hash → deterministic integer seed from any string.
// Used to turn (dateStr + programId + templateKey) into a repeatable shuffle seed.
// Same inputs → same exercises. Different day → fresh random selection.
const strHash = (s) => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return h >>> 0;
};

// Build a multi-muscle template by allocating exercise count per muscle group.
// `seed` > 0 shuffles each allocation slot's pool independently using a seeded PRNG —
// same seed = same exercises (reproducible), different seed = different exercises (variety).
// seed=0 (default) preserves blueprint definition order for the static module-load build.
// Each returned template exposes `.rebuild(seed, overrideInitialUsed?)` so Strength.jsx
// can regenerate it daily without re-importing allocation configs.
const buildTemplate = (title, allocations, initialUsed = null, seed = 0, opts = {}) => {
  const { ownsEquipment = null, trainsCombatives = false, baselineOnly = false, experience = null, fullGym = false } = opts;
  const eligible = makeEligible(trainsCombatives, experience);
  // Free tier (trial expired) draws only from the baseline library → a visibly
  // narrower plan than the trial/paid full-library experience. Paid / in-trial =
  // full library = wide variety. This is the primary "why upgrade" signal.
  const inLibrary = (e) => !baselineOnly || isBaselineExercise(e);
  // Equipment IDs that classify an exercise as "band-only" for full-gym deprioritization.
  const BAND_EQUIPMENT = new Set(['bands', 'mini-band']);
  const isBandOnly = (e) => Array.isArray(e.equipment) && e.equipment.length > 0
    && e.equipment.every((eq) => BAND_EQUIPMENT.has(eq));
  // Pool prioritization: two independent passes applied in order.
  //   1. Combatives users: float combat-beneficial moves to the FRONT — so a BJJ
  //      user's Chest T1 pool leads with Power Clean over Barbell Bench Press.
  //   2. Full-gym users: float band-ONLY exercises to the END — when a cable/machine
  //      alternative exists for the slot, it wins. When NO non-band alternative exists
  //      (e.g. Lateral Band Walk, Clamshell — Category B exercises with no machine
  //      equivalent), the band exercise still appears as the only eligible pick.
  //      This prevents the shuffle from ever serving "Band Bicep Curl" to someone
  //      standing next to a cable stack, while correctly keeping lateral hip-band work
  //      visible regardless of gym access.
  const prioritize = (arr) => {
    let sorted = arr;
    if (trainsCombatives) {
      sorted = [...sorted].sort((a, b) => (isCombativesBeneficial(b) ? 1 : 0) - (isCombativesBeneficial(a) ? 1 : 0));
    }
    if (fullGym) {
      sorted = [...sorted].sort((a, b) => (isBandOnly(a) ? 1 : 0) - (isBandOnly(b) ? 1 : 0));
    }
    return sorted;
  };
  const used = new Set(initialUsed || []);
  const blocks = [];
  allocations.forEach(({ muscle, category, count, primaryFilter, tier = null, phase = null, sets = null, exerciseId = null, skipEquipGate = false, isLumbarSlot = false }, slotIdx) => {
    // `phase` decouples "which session-length unlocks this slot" from the
    // exercise's own tier ranking (founder spec Jul 2026 — tier is a within-
    // muscle value ranking, NOT a breadth-inclusion switch). Defaults to
    // `tier` so every program that never sets `phase` behaves exactly as
    // before (T1 unlocks 30min, T2 unlocks 45min, T3 unlocks 60min). Full
    // Body slots override it explicitly — e.g. a T1 Calves slot can be held
    // back to phase 2 (45min) even though the exercise itself is tier 1.
    const effectivePhase = phase !== null ? phase : tier;

    // Static pinned-exercise path (Option B free-tier templates, Jul 2026):
    // If an allocation carries `exerciseId`, try to resolve that specific exercise
    // directly — skipping the pool/shuffle entirely. Still enforces all eligibility
    // gates (experience floor, library gate, equipment, dedup) so a free user who
    // has an injury flag or lacks the required equipment falls through to the normal
    // pool path as a graceful fallback, rather than silently serving an inaccessible move.
    if (exerciseId !== null) {
      const pinned = exercises.find((e) => e.id === exerciseId);
      // skipEquipGate: true lets a specific slot bypass the equipment ownership check.
      // Used on Pull-Up/Chin-Up in free-pull so the exercises render even without a bar —
      // the UI shows a "Today's Adjustments" note explaining the substitution.
      const equipOk = skipEquipGate || !ownsEquipment || !pinned?.equipment?.length || ownsEquipment(pinned);
      if (pinned && eligible(pinned) && inLibrary(pinned) && !used.has(pinned.name) && equipOk) {
        used.add(pinned.name);
        blocks.push({ refId: pinned.id, phase: effectivePhase, sets });
        return; // slot filled — skip normal pool logic
      }
      // Pinned exercise failed one or more checks → fall through to normal pool below
    }

    const baseFilter = category
      ? (e) => e.category === category
      : (e) => e.muscle === muscle;
    const filter = primaryFilter
      ? (e) => baseFilter(e) && primaryFilter(e)
      : baseFilter;
    // Per-slot deterministic shuffle (seed>0): XOR with a large prime so each slot
    // shuffles independently. seed=0 preserves definition order (static module load).
    const shuffle = (arr) => {
      if (!seed) return arr;
      let s = ((seed ^ (slotIdx * 2654435761)) >>> 0) || 1;
      const rand = () => { s ^= s << 13; s ^= s >> 17; s ^= s << 5; return (s >>> 0) / 0x100000000; };
      const out = [...arr];
      for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
      }
      return out;
    };
    // lumbarOnly: exercises tagged lumbarOnly:true (e.g. Back Extension) are excluded
    // from every general tier-based pool. They appear ONLY in slots that set
    // isLumbarSlot:true — the dedicated 60-min lumbar maintenance slot in back programs.
    // This prevents Back Extension from consuming a regular T1 Back slot while still
    // letting it appear in the explicit lumbar slot for users who own the bench.
    // The same gate is applied in tier-relaxation and equipment-relaxation paths below.
    let pool = exercises.filter(
      (e) => filter(e) && !used.has(e.name) && (tier === null || e.tier === tier)
        && eligible(e) && inLibrary(e) && !e.specialist && (!e.lumbarOnly || isLumbarSlot)
    );
    // Tier-relaxation fallback (founder spec Jul 22 2026 late evening): "if a T3
    // doesn't exist or fit, fix it by adding another T1 or T2" — rather than
    // silently dropping a tier-locked slot when the exact tier has nothing eligible
    // (common for a T3 slot once `baselineOnly` or `experience` narrows the pool),
    // relax to the next tier DOWN (T3→T2→T1) for the same muscle/filter, preferring
    // the closest tier first. Never relaxes UPWARD (a T1 slot never hands out a T3
    // finisher) — the point is "use a lesser depth-add," not "use a harder one."
    if (tier !== null && pool.length === 0) {
      for (let relaxedTier = tier - 1; relaxedTier >= 1 && pool.length === 0; relaxedTier--) {
        pool = exercises.filter(
          (e) => filter(e) && !used.has(e.name) && e.tier === relaxedTier
            && eligible(e) && inLibrary(e) && !e.specialist && (!e.lumbarOnly || isLumbarSlot)
        );
      }
    }
    // Equipment gate (universal, Jul 2026): only ever schedule movements the user
    // can actually perform with their gear. Bodyweight (no equipment) is always
    // allowed. When the tier-matched OWNED pool can't fill the slot, relax the tier
    // (still owned, same muscle) before giving up — a lighter/heavier owned move
    // beats gear the user doesn't have. If even that comes up empty the slot simply
    // yields fewer moves (NEVER an un-owned one); the Strength page's breath-work
    // fallback covers the rare all-empty day. When ownsEquipment is null (static
    // module-load build / Dashboard preview) behavior is unchanged.
    if (ownsEquipment) {
      // Equipment gate (Aug 2026 update): separate equipment-based exercises from bodyweight
      // (equipment:[]) so gym users never see towel curls, floor lat pulldowns, etc. when
      // cable/dumbbell/machine alternatives exist. Bodyweight fills slots only when NO
      // owned-equipment exercise is available — exact same logic as the existing band
      // deprioritization, extended to all equipment:[] exercises.
      const isBodyweightEx = (e) => Array.isArray(e.equipment) && e.equipment.length === 0;
      const equippedOwned = shuffle(pool.filter((e) => !isBodyweightEx(e) && ownsEquipment(e)));
      const bwOwned       = shuffle(pool.filter(isBodyweightEx));
      let owned = equippedOwned.length >= count
        ? equippedOwned
        : [...equippedOwned, ...bwOwned];
      if (owned.length < count) {
        const have = new Set(owned.map((e) => e.id));
        const relaxed = shuffle(
          exercises.filter((e) => filter(e) && !used.has(e.name) && eligible(e) && inLibrary(e)
            && ownsEquipment(e) && !have.has(e.id) && !e.specialist && (!e.lumbarOnly || isLumbarSlot))
        );
        owned = [...owned, ...relaxed];
      }
      pool = owned;
    } else {
      pool = shuffle(pool);
    }
    prioritize(pool).slice(0, count).forEach((p) => {
      used.add(p.name);
      blocks.push({
        refId: p.id,
        ...(effectivePhase !== null ? { phase: effectivePhase } : {}),
        // Explicit per-slot set count (Full Body's fixed 2/3 sets, independent
        // of experience level — founder spec Jul 2026). Falls through to the
        // tier×experience PRESCRIPTIONS default when a slot doesn't set it.
        ...(sets !== null ? { sets } : {}),
      });
    });
  });
  const seen = new Set();
  const deduped = blocks.filter((b) => (seen.has(b.refId) ? false : (seen.add(b.refId), true)));
  const template = {
    title,
    muscles: Array.from(new Set(allocations.map((a) => a.category ? 'Full Body' : a.muscle))),
    blocks: deduped,
  };
  // Attach a rebuild method so callers can regenerate with a fresh seed without
  // needing access to the original allocation config.
  template.rebuild = (newSeed, overrideInitialUsed = null, rebuildOpts = {}) =>
    buildTemplate(title, allocations, overrideInitialUsed !== null ? overrideInitialUsed : initialUsed, newSeed, rebuildOpts);
  return template;
};

const isBicepsEx = (e) => e.primaryMuscles?.includes('biceps');
const isTricepsEx = (e) => e.primaryMuscles?.includes('triceps');
const isForearmsEx = (e) => e.primaryMuscles?.includes('forearms');
const isQuadsEx = (e) => e.primaryMuscles?.includes('quads');
// Vertical pulls: pull-ups, chin-ups, lat pulldowns, straight-arm pulldowns
const isVerticalPullEx = (e) => /Pull-Up|Chin-Up|Pulldown/i.test(e.name);
// Horizontal pulls: rows of any kind
const isHorizontalPullEx = (e) => /Row/i.test(e.name);
// Hamstrings slot differentiation (Lower day, research-grounded Jul 2026):
// Research consensus across all experience levels: ONE heavy hip-hinge compound
// (RDL, Trap Bar DL, Conventional DL) + ONE machine knee-flexion curl per session.
// Without these filters the T1×2 slot structure picks two deadlift-family exercises
// back-to-back — same joints, same stimulus, not what a real coach programs.
const isHipHingeEx = (e) => /Deadlift|Good Morning|RDL/i.test(e.name);
const isLegCurlEx = (e) => /Leg Curl/i.test(e.name);
// Chest slot differentiation (PPL Push Day, Aug 2026):
// isChestPressEx — compound pressing movements only; prevents Fly/Pec Deck from
//   landing in the T1 press slots where research prescribes a compound.
// isChestIsoEx — fly/Pec Deck/crossover movements only; used for the 45min iso slot
//   so it never competes with or duplicates the press slots.
const isChestPressEx        = (e) => e.primaryMuscles?.includes('chest') && /Press|Bench|Dip/i.test(e.name);
const isChestIsoEx          = (e) => e.primaryMuscles?.includes('chest') && /Fly|Pec Deck|Crossover/i.test(e.name);
// Angle-specific Chest press filters (Aug 2026):
// Incline presses use primaryMuscles: ['upper-chest'] in the library; flat/standard
// presses use primaryMuscles: ['chest']. Splitting the two T1 Chest slots by angle
// guarantees the 30min session always pairs one flat + one incline compound.
// Without this, the engine can pick Machine Chest Press + BB Bench Press (both flat,
// both central pec) — technically valid tier picks but biomechanically redundant.
const isChestFlatPressEx    = (e) => isChestPressEx(e) && !e.primaryMuscles?.includes('upper-chest');
const isChestInclinePressEx = (e) => isChestPressEx(e) &&  e.primaryMuscles?.includes('upper-chest');
// Shoulder slot differentiation (PPL Push Day, Aug 2026):
// Push Day Shoulders = pressing movements ONLY (OHP, DB Press, Machine Press, Arnold
// Press, Push Press, Landmine Press). All raise-type work — lateral, front, rear
// delt, upright row, shrugs — belongs exclusively on Pull Day. Those are pulling
// motions biomechanically and fatigue the same posterior chain Pull Day trains.
// Founder direction Aug 2026: lateral raises are pulling movements, not push.
const isShoulderPressEx = (e) => e.primaryMuscles?.includes('shoulders') && /Press|OHP|Jerk|Landmine/i.test(e.name);
// Pull Day shoulder accessories (Aug 2026):
// Lateral raises (abduction) and rear-delt/trap work are biomechanically pulling
// motions — founder direction: they live on Pull Day, NOT Push Day.
// isLateralRaiseEx — medial delt width: DB Lateral Raise (T1), Cable (T2), Band (T3)
// isRearDeltEx     — posterior chain health: Face Pull, Rear Delt Fly, Shrugs, Upright Row
const isLateralRaiseEx = (e) => /Lateral Raise/i.test(e.name);
const isRearDeltEx     = (e) => e.primaryMuscles?.includes('rear-delts') || e.primaryMuscles?.includes('traps');
// isShoulderIsolationEx — combined lateral + rear-delt pool (excludes Shrugs which have a
// dedicated trap slot). Used for combined-pool isolation slots so the engine can pick from
// EITHER category in any order, with the weekly seed driving order variation for progressive
// overload (rotating which deltoid head fatigues first changes the stimulus across weeks).
const isShoulderIsolationEx = (e) => isLateralRaiseEx(e) || (isRearDeltEx(e) && !/Shrug/i.test(e.name));
// Core Day exercise filter (PPL Core Day, Aug 2026):
// Excludes movements that are inappropriate for a general-population Core Day:
//   - L-Sit / Tuck L-Sit: gymnastics-specific skill, requires exceptional shoulder + lat
//     strength that the average user won't have. Founder direction: remove from rotation.
//   - Dead Bug: PT/rehab context; not a gym-standard Core Day exercise.
//   - Pallof Press (all variants): "exotic" anti-rotation move; founder direction: not
//     appropriate for general Core Day programming.
//   - conditioning / combatives tagged exercises (Heavy Bag Combos etc.): Endurance
//     activities that have Core as a secondary muscle — should not appear in Core Day slots.
// Sport-specific combatives tags — exercises carrying any of these are grappling/
// striking technique drills, not general strength/core exercises.
const COMBATIVES_SPORT_TAGS = ['bjj', 'grappling', 'wrestling', 'boxing', 'neck-strength', 'muay-thai', 'judo'];

const isCoreExForDay = (e) =>
  e.muscle === 'Core' &&
  // Exclude gymnastics-specialist or rehab-specific exercises by name:
  !/L-Sit|Tuck.*L|Dead Bug|Pallof/i.test(e.name) &&
  // Exclude boxing/bag combo drills by name (e.g. Heavy Bag Boxing Combos):
  !/Boxing.*Combo|Bag.*Combo|Bag.*Kick|Knee Strike/i.test(e.name) &&
  // Exclude conditioning-circuit exercises (Mountain Climbers, etc.):
  !e.tags?.includes('conditioning') &&
  // Exclude sport-technique exercises (Hip Escape, Neck Bridge, etc.) by tag:
  !COMBATIVES_SPORT_TAGS.some((t) => e.tags?.includes(t));

// Compute the set of exercise names picked by Full Body A so B can avoid them.
// Mirrors Full Body A's allocation slots 1:1 (founder spec Jul 2026 reauthor)
// so B always gets distinct exercises for every muscle+tier+filter A could pick.
const fullBodyAUsed = (() => {
  const seen = new Set();
  // Each entry matches exactly one allocation slot in Full Body A, in order.
  [
    { muscle: 'Quads', tier: 1, filter: isQuadsEx },       // 1. Quads T1
    { muscle: 'Quads', tier: 2, filter: isQuadsEx },       // 2. Quads T2 (depth)
    { muscle: 'Hamstrings', tier: 1 },                    // 3. Hamstrings T1
    { muscle: 'Calves', tier: 1 },                        // 4. Calves T1 (breadth, 45min)
    { muscle: 'Chest', tier: 1 },                         // 5. Chest T1
    { muscle: 'Chest', tier: 2 },                         // 6. Chest T2 (depth)
    { muscle: 'Back', tier: 1 },                          // 7. Back T1
    { muscle: 'Shoulders', tier: 1 },                     // 8. Shoulders T1
    { muscle: 'Arms', tier: 1, filter: isTricepsEx },     // 9. Triceps T1 (breadth, 45min)
    { muscle: 'Arms', tier: 3, filter: isTricepsEx },     // 10. Triceps T3 (depth/finisher)
    { muscle: 'Core', tier: 1 },                          // 11. Core T1
  ].forEach(({ muscle, tier, filter }) => {
    // exercises is in tier + alphabetical order, so the first filtered result mirrors
    // what buildTemplate (seed=0) picks for the same slot in the static Full Body A.
    // (Strength.jsx uses seeded fresh templates where Full Body B gets its initialUsed
    //  recomputed dynamically via getTemplate — this static set is for Dashboard only.)
    const first = exercises
      .filter((e) => e.muscle === muscle && e.tier === tier && (!filter || filter(e)) && !seen.has(e.name))[0];
    if (first) seen.add(first.name);
  });
  return seen;
})();

export const programs = {
  ppl: {
    id: 'ppl',
    name: 'Push / Pull / Legs / Core',
    description: 'Classic 4/3 split. Push (chest/shoulders/triceps), Pull (back/biceps), Legs, Core.',
    bestFor: '3-6 days/week, intermediate+',
    minDays: 3,
    templates: {
      // PPL Push Day — research-grounded rebuild (Aug 2026).
      //
      // ORDERING RULE (non-negotiable, founder direction Aug 2026):
      //   ALL of a primary muscle's slots complete before ANY secondary muscle begins.
      //   Push Day order: Chest (T1 compound → T1 depth → iso) → Shoulders (press only)
      //   → Triceps (T1 → T2 → finisher). Interleaving is banned — a tired tricep
      //   on a late chest set gets a fraction of the hypertrophic stimulus.
      //
      // SHOULDER RULE (Aug 2026): Push Day carries PRESSING movements only.
      //   Lateral raises, front raises, rear delt work, upright rows, shrugs = Pull Day.
      //   They are pulling motions biomechanically and fatigue the posterior chain that
      //   Pull Day trains. isShoulderPressEx filter enforces this.
      //
      // SESSION LENGTH: phase gate maps to duration, not tier.
      //   phase:1 = 30 min floor · phase:2 = 45 min depth · phase:3 = 60 min depth.
      //
      // TIME CHECK (125s/set, 2min warm-up flat for all durations):
      //   30 min: (3+3+4+3)=13 sets × 125s = 27.1 + 2 = 29.1 min  ✓
      //   45 min: 13+(2+4+3)=22 sets × 125s = 45.8 + 2 = 47.8 min  ✓ (~3min over, accepted)
      //   60 min: 22+(3+3)  =28 sets × 125s = 58.3 + 2 = 60.3 min  ✓
      //
      // BALANCE: 3 Chest / 3 Shoulders / 3 Triceps at 60 min. Equal depth per muscle.
      push: buildTemplate('Push Day', [
        // ---- CHEST — all chest completed before any shoulder or tricep work ----
        // Angle-specific filters ensure the pair is always flat + incline (Aug 2026):
        // without them the engine can pick two flat presses (Machine + BB Bench),
        // which are biomechanically redundant (both central pec).
        { muscle: 'Chest',    count: 1, tier: 1,    phase: 1, sets: 3, primaryFilter: isChestFlatPressEx    },  // T1 flat — BB Bench / Machine Chest Press / Flat DB Press
        { muscle: 'Chest',    count: 1, tier: 1,    phase: 1, sets: 3, primaryFilter: isChestInclinePressEx },  // T1 incline — Incline BB / Incline DB (upper-chest primary)
        { muscle: 'Chest',    count: 1, tier: null, phase: 2, sets: 2, primaryFilter: isChestIsoEx          },  // Chest iso (45min) — Pec Deck / Cable Fly / DB Fly
        // ---- SHOULDERS — pressing movements only; all raises → Pull Day ----
        { muscle: 'Shoulders', count: 1, tier: 1,    phase: 1, sets: 4, primaryFilter: isShoulderPressEx }, // T1 press — OHP / DB Shoulder Press / Machine Press
        { muscle: 'Shoulders', count: 1, tier: 2,    phase: 2, sets: 4, primaryFilter: isShoulderPressEx }, // T2 press depth (45min) — Arnold Press / 2nd press variant
        { muscle: 'Shoulders', count: 1, tier: null, phase: 3, sets: 3, primaryFilter: isShoulderPressEx }, // 3rd press depth (60min) — Push Press / any T1/T2 variant
        // ---- TRICEPS — compound → isolation order ----
        // CGBP is allowed at T1 (30min) because it's the FIRST Triceps move: chest and
        // triceps are worked but not exhausted, so the compound engagement is an asset.
        // At T2 (45min adds), chest has done 3 exercises and triceps have already done
        // one set — CGBP here risks pre-exhausting both chains. Bench excluded in favour
        // of pure isolation (Skull Crusher / Kickback). Same reasoning × 2 at 60min.
        { muscle: 'Arms', count: 1, tier: 1,    phase: 1, sets: 3, primaryFilter: isTricepsEx },                                          // T1 30min — Rope Pushdown / OTE / CGBP eligible
        { muscle: 'Arms', count: 1, tier: 2,    phase: 2, sets: 3, primaryFilter: (e) => isTricepsEx(e) && !/Bench/i.test(e.name) }, // T2 45min — Skull Crusher / Kickback (no CGBP)
        { muscle: 'Arms', count: 1, tier: null, phase: 3, sets: 3, primaryFilter: (e) => isTricepsEx(e) && !/Bench/i.test(e.name) }, // finisher 60min — pure isolation (no CGBP)
      ]),
      // Pull Day timing (Aug 2026 rebuild with phases):
      //   30 min: (4+4+3) = 11 sets × 2.5 min/set + 2 warmup = 29.5 min ✓
      //   45 min: 11+(3+3) = 17 sets × 2.5 + 2 = 44.5 min ✓
      //   60 min: 17+(3+3) = 23 sets × 2.5 + 2 = 59.5 min ✓
      //
      // MUSCLE ORDER: all Back compounds first, then shoulder accessories, then Biceps
      // (Biceps assist every pull — keep them last so they're not pre-exhausted).
      // Shoulder raises/rear-delt work lives on Pull Day per founder spec Aug 2026:
      // lateral raises, rear-delt flies, face pulls, and shrugs are all pulling motions.
      // Pull Day — Aug 2026 rebuild (ordering + T3 finisher depth):
      // RULE: within every phase, declare Back → Shoulders → Biceps (in that order).
      // Shoulders before Biceps in every duration because Biceps assist all pulling
      // motions; pre-exhausting them limits shoulder raise quality.
      //
      // TIME CHECK (125s/set avg; T3 isolation ~105s/set; + 2min warm-up):
      //   30 min: 4×3 = 12 sets × 2.5 + 2 = 32 min  (~2min over — accepted)
      //   45 min: 12+(3+3) = 18 × 2.5 + 2 = 47 min  (~2min over — accepted)
      //   60 min: 18 sets×2.5 + 8 sets×1.75 + 2 = 61 min  ✓  (10 exercises total)
      pull: buildTemplate('Pull Day', [
        // ---- 30min: Back×2 → Shoulders×1 → Biceps×1 ----
        { muscle: 'Back',      count: 1, tier: 1,    phase: 1, sets: 3, primaryFilter: isVerticalPullEx  },  // T1 — Pull-Up / Lat Pulldown
        { muscle: 'Back',      count: 1, tier: 1,    phase: 1, sets: 3, primaryFilter: isHorizontalPullEx }, // T1 — BB Row / Cable Row
        // isShoulderIsolationEx combines lateral + rear delt into a single pool.
        // The weekly seed determines which category (and which specific exercise) appears first —
        // some weeks lateral leads, some weeks rear delt leads. Dedup prevents the same exercise
        // repeating. This implements the progressive overload principle: varying which head
        // fatigues first changes the adaptation stimulus across weeks without any code change.
        { muscle: 'Shoulders', count: 1, tier: null, phase: 1, sets: 3, primaryFilter: isShoulderIsolationEx },  // isolation 1 (lateral or rear delt, seed-varied)
        { muscle: 'Arms',      count: 1, tier: 1,    phase: 1, sets: 3, primaryFilter: isBicepsEx             },  // T1 biceps — AFTER Shoulders
        // ---- 45min adds: Shoulders isolation depth → Biceps T2 ----
        { muscle: 'Shoulders', count: 1, tier: null, phase: 2, sets: 3, primaryFilter: isShoulderIsolationEx },  // isolation 2 (dedup picks the other head or a new variation)
        { muscle: 'Arms',      count: 1, tier: 2,    phase: 2, sets: 3, primaryFilter: isBicepsEx             },  // T2 — Preacher / Hammer / Incline Curl
        // ---- 60min adds: T3 finishers, Back → Shoulders → Biceps ----
        { muscle: 'Back',      count: 1, tier: 3,    phase: 3, sets: 2, primaryFilter: isVerticalPullEx       },  // Back T3 — Straight-Arm Pulldown / lat isolation
        // 60-min finisher slots: locked to 1 lateral + 1 rear delt for balance.
        // 30/45-min slots remain seed-varied (combined pool) for progressive overload.
        // 1-2 rear delts per session is the research-supported sweet spot for correcting
        // the near-universal rear delt deficit; 3+ is overcorrection and poor programming optics.
        { muscle: 'Shoulders', count: 1, tier: null, phase: 3, sets: 2, primaryFilter: isLateralRaiseEx                                          },  // lateral finisher (Cable Lateral after DB Lateral from earlier)
        { muscle: 'Shoulders', count: 1, tier: null, phase: 3, sets: 2, primaryFilter: (e) => isRearDeltEx(e) && !/Shrug/i.test(e.name)          },  // rear delt finisher (2nd rear delt; dedup picks distinct from 30/45-min pick)
        { muscle: 'Arms',      count: 1, tier: 3,    phase: 3, sets: 2, primaryFilter: isBicepsEx        },  // T3 biceps — Cable Curl (T3 specifically)
      ]),
      // ---- PPL Legs Day (Aug 2026 rebuild — research-grounded, no Core: PPL+C has Core Day) ----
      // Slot order = muscle-group order (Quads → Hamstrings → Glutes → Calves), all slots for
      // each muscle grouped together so the display order is always correct at every session length.
      // No warm-up deducted from working-set timing (founder direction Aug 2026):
      //   30min: 5 ex × (3+3+3+3+2) = 14 sets × 125s = 29.2 min ✓
      //   45min: +3 ex × (3+3+2)    = 22 sets × 125s = 45.8 min ✓
      //   60min: +2 ex × (3+3)      = 28 sets × 125s = 58.3 min ✓
      // Hamstrings capped at 2 exercises max (research consensus): always 1 hip-hinge + 1 leg-curl,
      // never stacked hip-hinges. isHipHingeEx / isLegCurlEx filters enforce the split.
      legs: buildTemplate('Leg Day', [
        // ---- QUADS — 30min ×2 → 45min depth → 60min isolation ----
        { muscle: 'Quads', count: 1, tier: 1,    phase: 1, sets: 3, primaryFilter: isQuadsEx },  // Squat (Barbell/Goblet/Hack)
        { muscle: 'Quads', count: 1, tier: 1,    phase: 1, sets: 3, primaryFilter: isQuadsEx },  // 2nd Quad T1 — Hack Squat / Leg Press (deduped)
        { muscle: 'Quads', count: 1, tier: 2,    phase: 2, sets: 3, primaryFilter: isQuadsEx },  // 45min — Walking Lunge / Bulgarian SS / Goblet Squat
        { muscle: 'Quads', count: 1, tier: 3,    phase: 3, sets: 3, primaryFilter: isQuadsEx },  // 60min — Leg Extension
        // ---- HAMSTRINGS — hip-hinge + leg-curl (both at 30min; 2-exercise cap, never stacked) ----
        { muscle: 'Hamstrings', count: 1, tier: 1, phase: 1, sets: 3, primaryFilter: isHipHingeEx }, // RDL / Trap Bar DL / Conventional DL
        { muscle: 'Hamstrings', count: 1, tier: 1, phase: 1, sets: 3, primaryFilter: isLegCurlEx },  // Lying / Seated Leg Curl
        // ---- GLUTES — 45min compound → 60min isolation depth ----
        { muscle: 'Glutes', count: 1, tier: 1,    phase: 2, sets: 3 },  // 45min — Hip Thrust / Machine Hip Thrust
        { muscle: 'Glutes', count: 1, tier: null, phase: 3, sets: 3 },  // 60min — Cable Kickback / Frog Pump (deduped)
        // ---- CALVES — 30min anchor → 45min depth (only leg day in PPL+C) ----
        { muscle: 'Calves', count: 1, tier: 1,    phase: 1, sets: 2 },  // Calf Raise
        { muscle: 'Calves', count: 1, tier: null, phase: 2, sets: 2 },  // 45min — Seated Calf Raise (deduped)
      ]),
      // ---- PPL Core Day (Aug 2026 rebuild — movement-pattern grounded, no Core folded into Legs) ----
      // No dedicated Core Day scenarios exist in research — PPL research folds core into Legs (Plank/HLR).
      // This session is Enferno's design, from exercise-science movement-pattern taxonomy.
      // SLOT ORDER RULE (Aug 2026): ALL T1 slots declared before ALL T2 slots so the engine
      // renders compounds first at every session length (30/45/60min). T1 phase:3 slots are
      // declared in the T1 block so they appear before T2 phase:1 slots in the full 60min session.
      // isCoreExForDay filter excludes: L-Sit variants, Dead Bug, Pallof Press, conditioning/combatives.
      //
      // ENFERNO CORE WORKOUT — single 25–30 min session (Aug 2026 reset — founder direction).
      // Research consensus: 15–25 min is the realistic max for high-quality core work.
      // A 60-min Core session is unrealistic for 99% of users. No duration branching.
      // All slots at phase: 1 — session length pills are harmless no-ops for Core Day.
      //
      // Timing (5 exercises × ~3 sets, no warm-up deducted):
      //   3+3+3+3+2 = 14 sets × 125s/set ≈ 29 min  ✓
      // Paid: engine rotates all 5 slots through full Core library each week —
      //   experience-gated, injury-aware, history-deduplicated. Not static.
      core: buildTemplate('Core Day', [
        // ════ T1 SLOTS FIRST ════
        { muscle: 'Core', count: 1, tier: 1, phase: 1, sets: 3, primaryFilter: isCoreExForDay },  // T1 compound — Cable Crunch, Crunch, Mountain Climbers, Plank
        { muscle: 'Core', count: 1, tier: 1, phase: 1, sets: 3, primaryFilter: isCoreExForDay },  // T1 functional / carry — Suitcase Carry, Turkish Get-Up (int+), Side Plank
        // ════ T2 SLOTS SECOND ════
        { muscle: 'Core', count: 1, tier: 2, phase: 1, sets: 3, primaryFilter: isCoreExForDay },  // T2 hip-flexion ab — Sit-Up, Floor Leg Raise, Hanging Leg Raise (int+)
        { muscle: 'Core', count: 1, tier: 2, phase: 1, sets: 3, primaryFilter: isCoreExForDay },  // T2 anti-extension — Ab Wheel (int+), Plank Shoulder Tap, Bicycle Crunch
        { muscle: 'Core', count: 1, tier: 2, phase: 1, sets: 2, primaryFilter: isCoreExForDay },  // T2 rotational finisher — Bicycle Crunch, Russian Twist, Floor Leg Raise
      ]),
      // ---- PPL Free Static Template: Push Day (Aug 2026) ----
      // Reuse-first principle: every exerciseId here was already used in free-a / free-b
      // (Full Body static templates) before any new baseline addition was made.
      // Only Pec Deck (10) is a new baseline entry — it's the canonical chest iso
      // with no prior free-template equivalent. All other 8 exercises are pre-existing.
      //
      // Slot 6 (Push Press, id 82) is intermediate/advanced-only — beginners on PPL Push
      // at 60 min skip it and run ~54 min (accepted: PPL is intermediate+ per program spec).
      //
      // TIME CHECK (125s/set, 2min warm-up):
      //   30 min: (3+3+4+3)=13 sets × 125s = 27.1 + 2 = 29.1 min  ✓
      //   45 min: 13+(2+4+3)=22 × 125s = 45.8 + 2 = 47.8 min  ✓
      //   60 min int/adv: 22+(3+3)=28 × 125s = 58.3 + 2 = 60.3 min  ✓
      //   60 min beginner: 22+3=25 × 125s = 52.1 + 2 = 54.1 min  (accepted — PPL is int+)
      'free-push': buildTemplate('Push Day', [
        // ---- CHEST ----
        { muscle: 'Chest',     count: 1, tier: null, phase: 1, sets: 3, exerciseId: 9   },  // Machine Chest Press (free-a anchor)
        { muscle: 'Chest',     count: 1, tier: null, phase: 1, sets: 3, exerciseId: 3   },  // Incline DB Press (free-b anchor)
        { muscle: 'Chest',     count: 1, tier: null, phase: 2, sets: 2, exerciseId: 10  },  // Pec Deck (chest iso — new baseline entry)
        // ---- SHOULDERS — press only ----
        { muscle: 'Shoulders', count: 1, tier: null, phase: 1, sets: 4, exerciseId: 76  },  // Machine Shoulder Press (free-a anchor)
        { muscle: 'Shoulders', count: 1, tier: null, phase: 2, sets: 4, exerciseId: 69  },  // Seated DB Shoulder Press (free-b anchor)
        { muscle: 'Shoulders', count: 1, tier: null, phase: 3, sets: 3, exerciseId: 82  },  // Push Press (60min depth, intermediate+ only — beginner skips)
        // ---- TRICEPS ----
        { muscle: 'Arms',      count: 1, tier: null, phase: 1, sets: 3, exerciseId: 109 },  // Overhead Tricep Extension — first Triceps move (30min)
        { muscle: 'Arms',      count: 1, tier: null, phase: 2, sets: 3, exerciseId: 106 },  // Tricep Rope Pushdown — depth add (45min)
        { muscle: 'Arms',      count: 1, tier: null, phase: 3, sets: 3, exerciseId: 110 },  // Skull Crusher — pure iso finisher (60min); replaces CGBP which
        //   requires chest/shoulder stabilization when both are already spent
      ]),
      // Pull Day free static template (Aug 2026):
      // All 7 exercises are pre-existing baseline entries — no new additions needed.
      // TIME CHECK (125s/set + 2min warm-up):
      //   30 min: (4+4+3) = 11 sets × 2.5min + 2 = 29.5min ✓
      //   45 min: 11+(3+3) = 17 × 2.5 + 2 = 44.5min ✓
      //   60 min: 17+(3+3) = 23 × 2.5 + 2 = 59.5min ✓
      // NOTE: Previously `free-pull` was only referenced in PPL_FREE_MAP but never defined —
      // free users fell through to the paid dynamic template (both accounts looked identical).
      // This template fixes that gap.
      // Pull Day free static template — revised (Aug 2026):
      // 4 exercises at 30min × 3 sets = 12 sets (target density).
      // Lateral Raise moved to 30min as 4th compound-adjacent movement;
      // Rear Delt Fly cascades to 45min; Straight-Arm Pulldown added at 60min for lat depth.
      // TIME CHECK (125s/set + 2min warm-up):
      //   30 min: 4×3 = 12 sets × 2.5 + 2 = 32 min  (~2min over — accepted)
      //   45 min: 12+(3+3) = 18 × 2.5 + 2 = 47 min  (~2min over — accepted)
      //   60 min: 18+(3+3) = 24 × 2.5 + 2 = 62 min  (~2min over — accepted)
      // All 8 IDs are pre-existing baseline entries — zero new additions.
      // free-pull — Aug 2026 rebuild (ordering fix + T3 finisher depth + 10 exercises):
      // RULE APPLIED: within each phase, Back → Shoulders → Biceps (same as paid).
      // TIME CHECK (125s/set avg; T3 isolation ~105s/set; + 2min warm-up):
      //   30 min: 4×3 = 12 sets × 2.5 + 2 = 32 min  (~2min over — accepted)
      //   45 min: 12+(3+3) = 18 × 2.5 + 2 = 47 min  (~2min over — accepted)
      //   60 min: 18×2.5 + 8×1.75 + 2 = 61 min  ✓  (10 exercises total)
      // All 10 IDs are pre-existing baseline entries — zero new additions needed.
      'free-pull': buildTemplate('Pull Day', [
        // ---- 30min: Back×2 → Shoulders×1 → Biceps×1 ----
        { muscle: 'Back',      count: 1, tier: null, phase: 1, sets: 3, exerciseId: 37  },  // Lat Pulldown — vertical anchor
        { muscle: 'Back',      count: 1, tier: null, phase: 1, sets: 3, exerciseId: 53  },  // Seated Cable Row — horizontal anchor
        { muscle: 'Shoulders', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 71  },  // Lateral Raise — Shoulders BEFORE Biceps
        { muscle: 'Arms',      count: 1, tier: null, phase: 1, sets: 3, exerciseId: 103 },  // Dumbbell Curl — AFTER Shoulders
        // ---- 45min adds: Shoulders rear delt → Biceps compound depth ----
        { muscle: 'Shoulders', count: 1, tier: null, phase: 2, sets: 3, exerciseId: 74  },  // Rear Delt Fly — Shoulders FIRST
        { muscle: 'Arms',      count: 1, tier: null, phase: 2, sets: 3, exerciseId: 104 },  // Hammer Curl — neutral grip, targets brachialis/brachioradialis vs. DB Curl's biceps brachii
        // ---- 60min adds: T3 finishers, Back → Shoulders → Biceps ----
        { muscle: 'Back',      count: 1, tier: null, phase: 3, sets: 2, exerciseId: 39  },  // Straight-Arm Pulldown — lat isolation (T3 Back)
        { muscle: 'Shoulders', count: 1, tier: null, phase: 3, sets: 2, exerciseId: 40  },  // Face Pull — rear-delt + rotator cuff
        { muscle: 'Shoulders', count: 1, tier: null, phase: 3, sets: 2, exerciseId: 72  },  // Cable Lateral Raise — shoulder T3 finisher
        { muscle: 'Arms',      count: 1, tier: null, phase: 3, sets: 2, exerciseId: 105 },  // Cable Curl — T3 biceps finisher (last)
      ]),
      // ---- PPL Free Static Template: Legs Day (Aug 2026) ----
      // Reuse-first: Goblet Squat (127) from free-a, Leg Press (130) + Lying Leg Curl (163)
      // from free-b, Walking Lunge (129) from free-lower, Hip Thrust (178) + Calf Raise (134) +
      // Seated Calf Raise (239) + Glute Bridge (179) all pre-existing baseline entries.
      // Leg Extension (131) pinned-bypass (not in baseline, but pinned IDs skip baseline filter).
      // Lying Leg Curl (163) added to BASELINE_EXERCISE_IDS (fundamental machine exercise).
      // Timing (no warm-up deducted — founder direction Aug 2026):
      //   30min: 5 ex × (3+3+3+3+2) = 14 sets × 125s = 29.2 min ✓
      //   45min: +3 ex × (3+3+2)    = 22 sets × 125s = 45.8 min ✓
      //   60min: +2 ex × (3+3)      = 28 sets × 125s = 58.3 min ✓
      'free-legs': buildTemplate('Leg Day', [
        // ---- QUADS: 30min ×2 → 45min depth → 60min isolation ----
        { muscle: 'Quads', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 127 },  // Goblet Squat (free-a anchor)
        { muscle: 'Quads', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 130 },  // Leg Press (free-b anchor)
        { muscle: 'Quads', count: 1, tier: null, phase: 2, sets: 3, exerciseId: 129 },  // Walking Lunge — 45min quad depth
        { muscle: 'Quads', count: 1, tier: null, phase: 3, sets: 3, exerciseId: 131 },  // Leg Extension — 60min isolation
        // ---- HAMSTRINGS: hip-hinge + leg-curl (both at 30min) ----
        { muscle: 'Hamstrings', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 176 },  // Dumbbell RDL — hip-hinge anchor
        { muscle: 'Hamstrings', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 163 },  // Lying Leg Curl — leg-curl anchor
        // ---- GLUTES: 45min compound → 60min depth ----
        { muscle: 'Glutes', count: 1, tier: null, phase: 2, sets: 3, exerciseId: 178 },  // Hip Thrust — 45min glutes anchor
        { muscle: 'Glutes', count: 1, tier: null, phase: 3, sets: 3, exerciseId: 179 },  // Glute Bridge — 60min depth
        // ---- CALVES: 30min anchor → 45min depth ----
        { muscle: 'Calves', count: 1, tier: null, phase: 1, sets: 2, exerciseId: 134 },  // Calf Raise
        { muscle: 'Calves', count: 1, tier: null, phase: 2, sets: 2, exerciseId: 239 },  // Seated Calf Raise — 45min depth
      ]),
      // ---- PPL Free Static Template: Core Day (Aug 2026) ----
      // Reuse-first: all IDs are pre-existing baseline entries. No cable required.
      // SLOT ORDER RULE: T1-tier exercises declared first (all phases), T2-tier second,
      // so the rendered session always shows compounds before accessories at every length.
      //   T1 exercises (by ex.tier in exercises.js): Crunch(215), Suitcase Carry(208),
      //     Plank(195), Side Plank(202), Mountain Climbers(214), Turkish Get-Up(209)
      //   T2 exercises: Sit-Up(390), Bicycle Crunch(391), Floor Leg Raise(389), Hollow Body Hold(203)
      // Timing (no warm-up deducted — founder direction Aug 2026):
      //   30min:  T1(3+3+3) + T2(3+2) = 14 sets × 125s = 29.2 min ✓
      //   45min:  T1(3+3+3+3) + T2(3+2+3+2) = 22 sets × 125s = 45.8 min ✓
      //   60min:  T1(3+3+3+3+3+3) + T2(3+2+3+2) = 28 sets × 125s = 58.3 min ✓
      // Beginner note: Hollow Body Hold (203) and Turkish Get-Up (209) are intermediate+ gated.
      // ENFERNO CORE WORKOUT — Free static version (Aug 2026 reset).
      // 5 pinned exercises, all at phase: 1 (always show — no duration branching).
      // Free users always get this same 5-exercise 25-min session.
      // Timing: 3+3+3+3+2 = 14 sets × 125s ≈ 29 min ✓
      //   T1-tier exercises first (Plank, Suitcase Carry, Crunch),
      //   then T2-tier exercises (Side Plank, Bicycle Crunch).
      // Exercise selection uses the approved plan table:
      //   Slot 1 — Plank (195):         T1, isometric anti-extension, no equipment
      //   Slot 2 — Suitcase Carry (208): T1, anti-lateral carry, dumbbell/KB
      //   Slot 3 — Crunch (215):         T1, hip-flexion compound, no equipment
      //   Slot 4 — Side Plank (202):     T1 isometric, lateral core, no equipment
      //   Slot 5 — Bicycle Crunch (391): T2, oblique/rotational finisher, no equipment
      // McGill Big-3 principle: anterior (Plank) + lateral (Side Plank) + dynamic (Crunch/Bicycle).
      'free-core': buildTemplate('Core Day', [
        // ════ T1-TIER EXERCISES FIRST ════
        { muscle: 'Core', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 195 },  // Plank (T1, isometric) — anti-extension anchor
        { muscle: 'Core', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 208 },  // Suitcase Carry (T1) — anti-lateral carry
        { muscle: 'Core', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 215 },  // Crunch (T1) — hip-flexion compound
        { muscle: 'Core', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 202 },  // Side Plank (T1, isometric) — lateral core
        // ════ T2-TIER EXERCISES SECOND ════
        { muscle: 'Core', count: 1, tier: null, phase: 1, sets: 2, exerciseId: 391 },  // Bicycle Crunch (T2) — oblique/rotational finisher
      ]),
      // PPL+C bodyweight-only templates removed (Aug 2026 — founder decision):
      // Research does not support single-muscle-group days for bodyweight-only users.
      // No-equipment and band-only users on PPL+C are redirected in Strength.jsx to
      // Calisthenics Circuit, Full Body, or Upper/Lower — all of which are research-backed
      // for this equipment profile. See MASTER_BACKLOG.md for rationale.
    },
    // Fixed 7-slot default rotation, anchored to startDate. 'rest' fills the off days.
    defaultRotation: ['push', 'pull', 'rest', 'legs', 'core', 'rest', 'rest'],
  },

  'upper-lower': {
    id: 'upper-lower',
    name: 'Upper / Lower',
    description: 'Alternating upper-body and lower-body days. Balanced, recoverable.',
    bestFor: '2-6 days/week, any level',
    minDays: 2,
    // Founder spec Jul 2026 reauthor (2nd program walked per WORKOUT_CONSTRUCTION_SPEC.md
    // §6b, after Full Body — see spec §9/§9.6 for the full design rationale, including the
    // Jul 22 evening correction to strict tier-by-duration gating). Split days favor DEPTH
    // over breadth (opposite of Full Body): a primary muscle (Chest/Back on Upper,
    // Quads/Hamstrings on Lower) finishes its ENTIRE T1→T2→T3 progression before the next
    // muscle starts — never interleaved.
    //   • STRICT TIER-BY-DURATION (founder correction Jul 22 evening — this is the
    //     load-bearing rule): 30min = T1 ONLY, everywhere, no exceptions. 45min = 30min's
    //     exact plan + genuine T2 additions. 60min = 45min's exact plan + genuine T3
    //     additions. A duration tier NEVER shows a higher tier than its own gate — the
    //     previous pass violated this for Chest/Back (T1+T2 both unlocked at 30min) and
    //     for Shoulders/Triceps/Biceps/Glutes/Calves/Core (an any-tier `tier:null` pool at
    //     phase 1 could hand a 30min session a T3 exercise). Both are fixed below.
    //   • Primary muscles (Chest+Back / Quads+Hamstrings — the two largest-energy-demand
    //     muscles per split day) get a REAL, DISTINCT exercise at every tier: 2 T1 slots
    //     at 30min (bigger muscle = more direct volume), +1 T2 slot at 45min, +1 T3 slot
    //     at 60min. By 60min each primary muscle has 4 genuinely different exercises.
    //   • Secondary muscles (Shoulders/Triceps/Biceps / Glutes/Calves/Core) get exactly 1
    //     slot locked to `tier: 1` at 30min (never higher — this is what fixes the leak),
    //     and a genuine SECOND slot — still any-tier (`tier: null`) for week-to-week
    //     variety, per the founder's earlier "even smaller muscles deserve T2/T3 variety"
    //     direction — but now correctly gated to `phase: 3` (60min) so it can never surface
    //     before 45min regardless of which tier the shuffle picks.
    //   • ORDER (locked): primary muscle-then-tier first (large→small), secondary muscles
    //     after, in the order authored below.
    //   • SETS: primary T1/T2/T3 slots and secondary's #1 (30min) slot are a fixed 2 sets.
    //     Secondary's #2 (60min-only) slot is a fixed 1 set — a genuine "bonus variety"
    //     exposure, not a full working set, which is what makes room for primary's full
    //     depth progression without blowing the 60min time budget (see time check below).
    //   • TIME CHECK (90s rest + ~35s work = 125s/set; duration-scaled warm-up ~2min at
    //     30min, ~4min at 45min, ~7min at 60min): 30min = 7 ex / 14 sets ≈29.2min +2 ≈31min.
    //     45min = 9 ex / 18 sets ≈37.5min +4 ≈41.5min. 60min = 12 ex / 24 sets ≈50min
    //     +7 ≈57min. Close to, never over, the advertised time.
    //     NOTE: Lower was revised Jul 2026 (research alignment pass): Hamstrings capped at
    //     2 slots (hip-hinge + leg curl, both at 30min — no depth-adds). Duration scaling
    //     adds Quad depth and a Glutes 45min slot instead. See spec §10.2.
    templates: {
      upper: buildTemplate('Upper Body', [
        // ---- Chest — full T1→T2→T3 before Back starts (2 T1 @30min — primary muscle) ----
        { muscle: 'Chest', count: 1, tier: 1, phase: 1, sets: 2 },                               // Chest T1 #1 — BB Bench
        { muscle: 'Chest', count: 1, tier: 1, phase: 1, sets: 2 },                               // Chest T1 #2 — Incline BB Bench
        { muscle: 'Chest', count: 1, tier: 2, phase: 2, sets: 2 },                               // Chest T2 (45min) — Incline DB Press
        { muscle: 'Chest', count: 1, tier: 3, phase: 3, sets: 2 },                               // Chest T3 (60min) — Chest Fly
        // ---- Back — full T1→T2→T3 (vertical pull T1, horizontal pull T1, then T2/T3) ----
        { muscle: 'Back', count: 1, tier: 1, phase: 1, sets: 2, primaryFilter: isVerticalPullEx },   // Back T1 vertical — Pull-Up/Lat Pulldown
        { muscle: 'Back', count: 1, tier: 1, phase: 1, sets: 2, primaryFilter: isHorizontalPullEx }, // Back T1 horizontal — BB Row/Chest-Supported Row
        { muscle: 'Back', count: 1, tier: 2, phase: 2, sets: 2 },                                // Back T2 (45min) — Cable Row
        { muscle: 'Back', count: 1, tier: 3, phase: 3, sets: 2 },                                // Back T3 (60min) — Face Pull
        // ---- Secondary muscles — locked to T1 at 30min; any-tier 2nd slot at 60min only ----
        { muscle: 'Shoulders', count: 1, tier: 1, phase: 1, sets: 2 },                           // Shoulders #1 (T1-only, 30min)
        { muscle: 'Shoulders', count: 1, tier: null, phase: 3, sets: 1 },                        // Shoulders #2 (any-tier depth, 60min)
        { muscle: 'Arms', count: 1, tier: 1, phase: 1, sets: 2, primaryFilter: isTricepsEx },    // Triceps #1 (T1-only, 30min)
        { muscle: 'Arms', count: 1, tier: null, phase: 3, sets: 1, primaryFilter: isTricepsEx }, // Triceps #2 (any-tier depth, 60min)
        { muscle: 'Arms', count: 1, tier: 1, phase: 1, sets: 2, primaryFilter: isBicepsEx },     // Biceps #1 (T1-only, 30min)
        { muscle: 'Arms', count: 1, tier: null, phase: 3, sets: 1, primaryFilter: isBicepsEx },  // Biceps #2 (any-tier depth, 60min)
      ]),
      lower: buildTemplate('Lower Body', [
        // Research grounding (Jul 2026): research consensus across ALL experience levels and
        // ALL durations shows Hamstrings capped at EXACTLY 2 exercises — 1 hip-hinge compound
        // + 1 machine leg curl. Duration scaling adds QUAD depth, never hamstring depth.
        // §9.6's Hamstrings T2 and T3 depth slots were removed; the budget is redistributed to
        // a Glutes depth-add at 45min (research-grounded: Hip Thrust / Bulgarian SS appears at
        // this tier in advanced scenarios) and upgraded 60min secondary slots (2 sets vs. 1).
        //
        // TIME CHECK (90s rest + ~35s work = 125s/set; duration-scaled warm-up):
        //   30min = 7 ex / 14 sets ≈ 29.2min + 2min = 31.2min.
        //   45min = 9 ex / 18 sets ≈ 37.5min + 4min = 41.5min.
        //   60min = 12 ex / 24 sets ≈ 50min + 7min = 57min. Close to, never over, the target.
        //
        // ---- Quads — 2 T1 at 30min, T2 at 45min, T3 at 60min ----
        { muscle: 'Quads', count: 1, tier: 1, phase: 1, sets: 2, primaryFilter: isQuadsEx },     // Quads T1 #1 — Back Squat
        { muscle: 'Quads', count: 1, tier: 1, phase: 1, sets: 2, primaryFilter: isQuadsEx },     // Quads T1 #2 — Front Squat / Hack Squat
        { muscle: 'Quads', count: 1, tier: 2, phase: 2, sets: 2, primaryFilter: isQuadsEx },     // Quads T2 (45min) — Goblet Squat / Leg Press
        { muscle: 'Quads', count: 1, tier: 3, phase: 3, sets: 2, primaryFilter: isQuadsEx },     // Quads T3 (60min) — Leg Extension / Bulgarian SS
        // ---- Hamstrings — 2 slots, 2 movement patterns, present from 30min, no depth-adds ----
        { muscle: 'Hamstrings', count: 1, tier: 1, phase: 1, sets: 2, primaryFilter: isHipHingeEx }, // Ham T1 hip-hinge — RDL / Trap Bar DL / Conventional DL
        { muscle: 'Hamstrings', count: 1, tier: 1, phase: 1, sets: 2, primaryFilter: isLegCurlEx },  // Ham T1 leg curl — Lying / Seated Leg Curl
        // ---- Glutes — T1 at 30min, any-tier depth at 45min (research: Hip Thrust / Bulgarian SS) ----
        { muscle: 'Glutes', count: 1, tier: 1, phase: 1, sets: 2 },                              // Glutes #1 (T1, 30min)
        { muscle: 'Glutes', count: 1, tier: null, phase: 2, sets: 2 },                           // Glutes #2 (any-tier, 45min — Hip Thrust variation / Machine Hip Thrust)
        // ---- Calves — T1 at 30min, any-tier depth at 60min ----
        { muscle: 'Calves', count: 1, tier: 1, phase: 1, sets: 2 },                              // Calves #1 (T1, 30min)
        { muscle: 'Calves', count: 1, tier: null, phase: 3, sets: 2 },                           // Calves #2 (any-tier, 60min — Seated Calf Raise / Single-Leg)
        // ---- Core — T1 at 30min, any-tier depth at 60min ----
        { muscle: 'Core', count: 1, tier: 1, phase: 1, sets: 2 },                                // Core #1 (T1, 30min)
        { muscle: 'Core', count: 1, tier: null, phase: 3, sets: 2 },                             // Core #2 (any-tier, 60min — Hollow Body / Ab Wheel)
      ]),
      // ---- Option B free bodyweight templates (Jul 2026) ----
      // Free + no-equipment users on Upper/Lower get these pinned static templates
      // instead of the equipment-dependent paid templates. Uses exclusively equipment:[]
      // IDs, grounded in RESEARCH_WORKOUT_SCENARIOS_2026-07-23.md bodyweight scenarios.
      //
      // Time model (founder-approved Aug 2026): 1 set ≈ 2.5 min (≈40s work + 90s rest).
      //   free-upper: T1 = 6 ex × 2 sets = 12 sets × 2.5 + 3 warm-up ≈ 33 min (30min target ✓)
      //               T2 = +3 ex × 2 sets = 18 sets total × 2.5 + 4.5 ≈ 49.5 min (45min target ✓)
      //               T3 = +3 ex × 2 sets = 24 sets total × 2.5 + 6   ≈ 66 min (60min target ✓)
      //   free-lower: T1 = 4 ex × 3 sets = 12 sets × 2.5 + 3 warm-up ≈ 33 min (30min target ✓)
      //               T2 = +3 ex × 2 sets = 18 sets total × 2.5 + 4.5 ≈ 49.5 min (45min target ✓)
      //               T3 = +3 ex × 2 sets = 24 sets total × 2.5 + 6   ≈ 66 min (60min target ✓)
      //
      // Muscle balance (Upper):
      //   T1: 2 chest compounds (horizontal push + incline push) · 2 back compounds
      //       (horizontal pull + posterior chain) · 1 shoulder press · 1 core
      //   T2: adds dedicated triceps · dedicated biceps · rear-delt/back complement
      //   T3: adds harder shoulder/chest depth · rotational core · core stability
      //
      // Muscle balance (Lower):
      //   T1: 2 quad-dominant compounds (glutes as secondary) · 1 calf · 1 core
      //   T2: adds quad mobility depth · dedicated glutes (not stacked at T1) · core variety
      //   T3: adds glute isolation depth · only no-equip hamstring isolation · lateral core
      // Slot order is MUSCLE-GROUP order (not phase order) so that T2/T3 additions
      // insert contextually within their muscle block rather than appending at the
      // bottom. The phase gate hides inapplicable slots; visible order mirrors the
      // array order — so Shoulders T2 + T3 appear right after Shoulders T1, etc.
      //
      // free-upper visible order by session length:
      //   30min: Push-Up, Incline PU, Doorframe Row, Superman, Elevated Pike PU, Floor Leg Raise
      //   45min: + Prone Y Raise (after shoulder block), Bench Dips, Towel Curl (after arms block)
      //   60min: + Pike Push-Up (after shoulder block), Bicycle Crunch, Plank Shoulder Tap (after core)
      //
      // free-lower visible order by session length:
      //   30min: BW Squat, Walking Lunge, Calf Raise, Floor Leg Raise
      //   45min: + ATG Split Squat (quad block), Glute Bridge (glute block), Sit-Up (core block)
      //   60min: + SL Glute Bridge (glute block), Nordic Curl* (hamstring), Side Plank (core block)
      //          * Nordic Curl gated to intermediate+; beginners see 9 exercises at 60min (by design)
      // Explicit `sets` on every slot (Aug 2026 fix): without these, getPrescription
      // defaults return 3 sets for beginner tier-1/2 exercises, overrunning the time budget.
      //   free-upper: all slots sets:2 → 6×2=12 sets T1 (30min), 9×2=18 T2 (45min), 12×2=24 T3 (60min)
      //   free-lower: T1 slots have NO sets override — prescription naturally gives 3 sets for
      //     beginner tier-2 exercises (BW Squat, Walking Lunge, Floor Leg Raise, Calf Raise) = 4×3=12 ✓
      //     T2 and T3 slots: sets:2 → +3×2=6 added at each tier; 18 total at 45min, 24 at 60min
      'free-upper': buildTemplate('Bodyweight Upper Body', [
        // — Chest block —
        { muscle: 'Chest',     count: 1, tier: null, phase: 1, sets: 2, exerciseId: 7   },  // Push-Up (T1 30min) — primary chest compound
        { muscle: 'Chest',     count: 1, tier: null, phase: 1, sets: 2, exerciseId: 388 },  // Incline Push-Up (T1 30min) — 2nd chest compound
        // — Back block —
        { muscle: 'Back',      count: 1, tier: null, phase: 1, sets: 2, exerciseId: 372 },  // Doorframe Row (T1 30min) — primary back compound
        { muscle: 'Back',      count: 1, tier: null, phase: 1, sets: 2, exerciseId: 373 },  // Superman (T1 30min) — 2nd back / posterior chain
        // — Shoulder block (T1 anchor + T2 + T3 all consecutive) —
        { muscle: 'Shoulders', count: 1, tier: null, phase: 1, sets: 2, exerciseId: 387 },  // Elevated Pike Push-Up (T1 30min) — overhead press
        { muscle: 'Shoulders', count: 1, tier: null, phase: 2, sets: 2, exerciseId: 386 },  // Prone Y Raise (T2 45min) — rear delt complement
        { muscle: 'Shoulders', count: 1, tier: null, phase: 3, sets: 2, exerciseId: 88  },  // Pike Push-Up (T3 60min) — overhead depth
        // — Arms block (T2 anchor + T2 both consecutive) —
        { muscle: 'Arms',      count: 1, tier: null, phase: 2, sets: 2, exerciseId: 108 },  // Bench Dips (T2 45min) — dedicated triceps
        { muscle: 'Arms',      count: 1, tier: null, phase: 2, sets: 2, exerciseId: 377 },  // Towel Curl (T2 45min) — dedicated biceps
        // — Core block (T1 anchor + T3 both consecutive) —
        { muscle: 'Core',      count: 1, tier: null, phase: 1, sets: 2, exerciseId: 389 },  // Floor Leg Raise (T1 30min) — lower abs / hip-flexors
        { muscle: 'Core',      count: 1, tier: null, phase: 3, sets: 2, exerciseId: 391 },  // Bicycle Crunch (T3 60min) — obliques + rectus
        { muscle: 'Core',      count: 1, tier: null, phase: 3, sets: 2, exerciseId: 385 },  // Plank Shoulder Tap (T3 60min) — stability
      ]),
      'free-lower': buildTemplate('Bodyweight Lower Body', [
        // — Quads block (T1 × 2; no T2/T3 quad slot — ATG Split Squat belongs in paid dynamic) —
        // Glutes are NOT a standalone T1 slot — secondary engagement from squats/lunges (founder direction Aug 2026)
        // T1 slots intentionally have NO sets override: getPrescription returns 3 sets for beginner
        // tier-2 exercises, which is exactly the budget (4 ex × 3 sets = 12 sets ≈ 30 min ✓).
        { muscle: 'Quads',      count: 1, tier: null, phase: 1, exerciseId: 378 },  // BW Squat (T1 30min) — primary quad compound
        { muscle: 'Quads',      count: 1, tier: null, phase: 1, exerciseId: 129 },  // Walking Lunge (T1 30min) — unilateral quad/glute
        // — Hamstrings block (T2 anchor: beginner hip-hinge; T3: intermediate+ isolation) —
        // BW Good Morning = research-prescribed beginner hip-hinge substitute for RDL.
        // Nordic Curl gated to intermediate+ by experience floor (beginner sees 9 ex at 60min ≈ 57 min ✓).
        { muscle: 'Hamstrings', count: 1, tier: null, phase: 2, sets: 2, exerciseId: 392 },  // BW Good Morning (T2 45min) — hip-hinge anchor
        { muscle: 'Hamstrings', count: 1, tier: null, phase: 3, sets: 2, exerciseId: 166 },  // Nordic Curl (T3 60min, intermediate+)
        // — Glutes block (T2 anchor + T3 depth) —
        { muscle: 'Glutes',     count: 1, tier: null, phase: 2, sets: 2, exerciseId: 179 },  // Glute Bridge (T2 45min) — dedicated glutes
        { muscle: 'Glutes',     count: 1, tier: null, phase: 3, sets: 2, exerciseId: 384 },  // SL Glute Bridge (T3 60min) — glute depth
        // — Calves block —
        { muscle: 'Calves',     count: 1, tier: null, phase: 1, exerciseId: 134 },  // Calf Raise (T1 30min)
        // — Core block (T1 anchor + T2 + T3 all consecutive) —
        { muscle: 'Core',       count: 1, tier: null, phase: 1, exerciseId: 389 },  // Floor Leg Raise (T1 30min) — lower abs
        { muscle: 'Core',       count: 1, tier: null, phase: 2, sets: 2, exerciseId: 390 },  // Sit-Up (T2 45min) — full-range ab compound
        { muscle: 'Core',       count: 1, tier: null, phase: 3, sets: 2, exerciseId: 202 },  // Side Plank (T3 60min) — lateral core
      ]),
    },
    defaultRotation: ['upper', 'lower', 'rest', 'upper', 'lower', 'rest', 'rest'],
  },

  'full-body': {
    id: 'full-body',
    name: 'Full Body',
    description: 'Two different Full Body options hit every major muscle group each session. Alternates day to day and week to week. Great for beginners and busy schedules.',
    bestFor: '1-4 days/week, any level',
    minDays: 1,
    // Founder spec Jul 2026 reauthor. Full Body combines a Lower Day + Upper
    // Day into one session, so ordering + gating both matter:
    //   • ORDER (locked, never changes — see buildTemplate: allocations array
    //     order IS block order, always; the daily seed only shuffles which
    //     exercise fills a slot, never the slot sequence): Lower body first
    //     (larger muscles, more energy demand), then Upper — muscle-then-tier
    //     within each region, large-to-small.
    //   • GATING (`phase`): tier ranks an exercise's value WITHIN a muscle —
    //     it is NOT a breadth switch. Every included muscle gets a genuine T1
    //     movement at 30 min. `phase` (independent of the exercise's own
    //     tier) controls which session length unlocks each slot: 1=30min,
    //     2=45min (breadth — new muscles), 3=60min (depth — a 2nd/3rd
    //     exercise on an already-included muscle). 45min never adds depth;
    //     60min never adds a brand-new muscle, only depth on Chest/Triceps/
    //     Quads (A) and Back/Biceps/Glutes (B) — a push-chain vs. pull-chain
    //     split so each day reads as coherent programming, not 8 unrelated
    //     muscles plus bonus exercises.
    //   • SETS: fixed per slot (independent of experience — experience gates
    //     which exercises are eligible, not set count), never inflating with
    //     duration: 2 sets on every T1 breadth slot, 3 sets on depth-adds.
    //     Real time check (90s default rest + ~35s work = 125s/set): 30min =
    //     6×2=12 sets ≈30min. 45min = 8×2=16 sets ≈38min. 60min = 8×2 + 3×3 =
    //     25 sets ≈57min. Never exceeds the advertised time.
    templates: {
      'full-a': buildTemplate('Full Body A', [
        // ---- Lower body (larger muscles, more energy demand — goes first) ----
        { muscle: 'Quads',       count: 1, tier: 1, phase: 1, sets: 2, primaryFilter: isQuadsEx },  // Quads T1 — Back Squat
        { muscle: 'Quads',       count: 1, tier: 2, phase: 3, sets: 3, primaryFilter: isQuadsEx },  // Quads T2 depth — Goblet Squat
        { muscle: 'Hamstrings', count: 1, tier: 1, phase: 1, sets: 2 },                            // Hamstrings T1 — Romanian DL
        { muscle: 'Calves',     count: 1, tier: 1, phase: 2, sets: 2 },                            // Calves T1 breadth (45min) — Standing Calf Raise
        // ---- Upper body — push-chain emphasis (Chest/Triceps get the depth-adds) ----
        { muscle: 'Chest',      count: 1, tier: 1, phase: 1, sets: 2 },                            // Chest T1 — BB Bench
        { muscle: 'Chest',      count: 1, tier: 2, phase: 3, sets: 3 },                            // Chest T2 depth — Incline DB Press
        { muscle: 'Back',       count: 1, tier: 1, phase: 1, sets: 2 },                            // Back T1 — Pull-Up
        { muscle: 'Shoulders',  count: 1, tier: 1, phase: 1, sets: 2 },                            // Shoulders T1 — OHP
        { muscle: 'Arms',       count: 1, tier: 1, phase: 2, sets: 2, primaryFilter: isTricepsEx }, // Triceps T1 breadth (45min) — Close-Grip Bench / Overhead Ext / Rope Pushdown
        { muscle: 'Arms',       count: 1, tier: null, phase: 3, sets: 3, primaryFilter: isTricepsEx }, // Triceps finisher (60min) — any unused triceps: Rope Pushdown, Skull Crusher, Kickback
        { muscle: 'Core',       count: 1, tier: 1, phase: 1, sets: 2 },                            // Core T1 — Suitcase Carry
      ]),
      'full-b': buildTemplate('Full Body B', [
        // ---- Lower body ----
        { muscle: 'Quads',       count: 1, tier: 1, phase: 1, sets: 2, primaryFilter: isQuadsEx },  // Quads T1 — Front Squat (A used Back Squat)
        { muscle: 'Hamstrings', count: 1, tier: 1, phase: 1, sets: 2 },                            // Hamstrings T1 — Trap Bar Deadlift
        { muscle: 'Glutes',     count: 1, tier: 2, phase: 3, sets: 3 },                            // Glutes T2 depth (60min) — Single-Leg Hip Thrust
        { muscle: 'Calves',     count: 1, tier: 1, phase: 2, sets: 2 },                            // Calves T1 breadth (45min) — Standing Calf Raise
        // ---- Upper body — pull-chain emphasis (Back/Biceps get the depth-adds) ----
        { muscle: 'Chest',      count: 1, tier: 1, phase: 1, sets: 2 },                            // Chest T1 — Dips (A used BB Bench)
        { muscle: 'Back',       count: 1, tier: 1, phase: 1, sets: 2 },                            // Back T1 — BB Bent-Over Row (A used Pull-Up)
        { muscle: 'Back',       count: 1, tier: 2, phase: 3, sets: 3 },                            // Back T2 depth — Cable Row
        { muscle: 'Shoulders',  count: 1, tier: 1, phase: 1, sets: 2 },                            // Shoulders T1 — Seated DB Press (A used OHP)
        { muscle: 'Arms',       count: 1, tier: 1, phase: 2, sets: 2, primaryFilter: isBicepsEx },  // Biceps T1 breadth (45min) — Barbell Curl / DB Curl / EZ Bar Curl
        { muscle: 'Arms',       count: 1, tier: null, phase: 3, sets: 3, primaryFilter: isBicepsEx },  // Biceps finisher (60min) — any unused biceps: Cable Curl, Bayesian, etc.
        { muscle: 'Core',       count: 1, tier: 1, phase: 1, sets: 2 },                            // Core T1 — Turkish Get-Up
      ], fullBodyAUsed),

      // --- Free-tier static templates (Option B, Jul 2026) ---
      // Each slot is pinned to a specific exercise via `exerciseId`. The engine serves
      // that exercise directly (skipping the shuffle) after verifying eligibility +
      // baseline membership + equipment ownership — falling back to normal pool logic
      // only if the pinned exercise fails a check (injury flag, missing equipment, etc.).
      // Exercises are research-grounded (Jul 23 2026 scenario research) and chosen to be
      // the most universally familiar, accessible movement for each slot. Paid users never
      // see these templates — they get the seeded-shuffle full-library dynamic templates above.
      //
      // Free-A: push-chain emphasis (Chest/Triceps get depth-adds at 60min)
      // Free-B: pull-chain emphasis (Back/Biceps get depth-adds at 60min)
      'free-a': buildTemplate('Full Body A', [
        // ---- Lower body (larger muscles first) ----
        { muscle: 'Quads',      exerciseId: 127, tier: 1, phase: 1, sets: 2, primaryFilter: isQuadsEx },  // Goblet Squat
        { muscle: 'Quads',      exerciseId: 139, tier: 2, phase: 3, sets: 3, primaryFilter: isQuadsEx },  // Reverse Lunge (depth, 60min)
        { muscle: 'Hamstrings', exerciseId: 176, tier: 1, phase: 1, sets: 2 },                            // Dumbbell Romanian Deadlift
        { muscle: 'Calves',     exerciseId: 134, tier: 1, phase: 2, sets: 2 },                            // Calf Raise (45min)
        // ---- Upper body — push-chain emphasis ----
        { muscle: 'Chest',      exerciseId: 9,   tier: 1, phase: 1, sets: 2 },                            // Machine Chest Press
        { muscle: 'Chest',      exerciseId: 4,   tier: 2, phase: 3, sets: 3 },                            // Flat Dumbbell Press (depth, 60min)
        { muscle: 'Back',       exerciseId: 37,  tier: 1, phase: 1, sets: 2 },                            // Lat Pulldown
        { muscle: 'Shoulders',  exerciseId: 76,  tier: 1, phase: 1, sets: 2 },                            // Machine Shoulder Press
        { muscle: 'Arms',       exerciseId: 109, tier: 1, phase: 2, sets: 2, primaryFilter: isTricepsEx }, // Overhead Tricep Extension (45min)
        { muscle: 'Arms',       exerciseId: 106, tier: null, phase: 3, sets: 3, primaryFilter: isTricepsEx }, // Tricep Rope Pushdown (finisher, 60min)
        { muscle: 'Core',       exerciseId: 195, tier: 1, phase: 1, sets: 2 },                            // Plank
      ]),
      // Free-B: pull-chain emphasis (Back/Biceps get depth-adds at 60min)
      // No initialUsed needed — free-a and free-b share no exercises by design.
      'free-b': buildTemplate('Full Body B', [
        // ---- Lower body ----
        { muscle: 'Quads',      exerciseId: 130, tier: 1, phase: 1, sets: 2, primaryFilter: isQuadsEx },  // Leg Press (distinct from A's Goblet Squat)
        { muscle: 'Hamstrings', exerciseId: 163, tier: 1, phase: 1, sets: 2 },                            // Lying Leg Curl (distinct from A's RDL)
        { muscle: 'Glutes',     exerciseId: 179, tier: 2, phase: 3, sets: 3 },                            // Glute Bridge (depth, 60min)
        { muscle: 'Calves',     exerciseId: 239, tier: 1, phase: 2, sets: 2 },                            // Seated Calf Raise (45min, distinct from A's standing)
        // ---- Upper body — pull-chain emphasis ----
        { muscle: 'Chest',      exerciseId: 3,   tier: 1, phase: 1, sets: 2 },                            // Incline Dumbbell Press (distinct from A's flat)
        { muscle: 'Back',       exerciseId: 53,  tier: 1, phase: 1, sets: 2 },                            // Seated Cable Row (horizontal pull, distinct from A's vertical)
        { muscle: 'Back',       exerciseId: 41,  tier: 2, phase: 3, sets: 3 },                            // Inverted Row (depth, 60min)
        { muscle: 'Shoulders',  exerciseId: 69,  tier: 1, phase: 1, sets: 2 },                            // Seated Dumbbell Press (distinct from A's machine)
        { muscle: 'Arms',       exerciseId: 103, tier: 1, phase: 2, sets: 2, primaryFilter: isBicepsEx },  // Dumbbell Curl (45min)
        { muscle: 'Arms',       exerciseId: 105, tier: null, phase: 3, sets: 3, primaryFilter: isBicepsEx }, // Cable Curl (finisher, 60min)
        { muscle: 'Core',       exerciseId: 199, tier: 1, phase: 1, sets: 2 },                            // Russian Twist (distinct from A's Plank)
      ]),
    },
    // A/B/A on even weeks; flips to B/A/B on odd weeks (computed in getRotationKey)
    defaultRotation: ['full-a', 'rest', 'full-b', 'rest', 'full-a', 'rest', 'rest'],
  },

  bro: {
    id: 'bro',
    name: 'Bro Split',
    description: 'One muscle group per day. Higher volume, more time for recovery between sessions.',
    bestFor: '4–6 days/week · intermediate+',
    minDays: 4,
    templates: {
      // ── Chest Day (5-day Bro Split paid, Aug 2026 redesign) ──
      // Standalone day — no secondary muscle.
      // Research cap (Aug 2026): 6 working exercises × 3 sets = 18 sets per session
      // sits in the evidence-based hypertrophy sweet spot (Schoenfeld meta-analysis,
      // RP Strength, Nippard programs all cite 15-20 sets/session as optimal for
      // intermediate-advanced single-muscle-group days). 8 exercises previously built
      // 24 sets — above the range where junk-volume risk increases.
      // 30 min: 3×T1 + 1×T2 = 12 sets ≈ 30 min
      // 45 min: +1×T2 iso + 1×T3 = 18 sets ≈ 45 min  (6 exercises, sweet spot)
      // 60 min: + 1×T3 finisher (2 sets) = 20 sets ≈ 60 min  (6 working + 1 low-volume finisher)
      chest: buildTemplate('Chest Day', [
        { muscle: 'Chest', count: 3, tier: 1, phase: 1, sets: 3 },                                       // T1 compounds (flat + incline + depth, engine picks 3)
        { muscle: 'Chest', count: 1, tier: 2, phase: 1, sets: 3 },                                       // T2 accessory (Incline DB / Machine Press / Flat DB)
        { muscle: 'Chest', count: 1, tier: 2, phase: 2, sets: 3, primaryFilter: isChestIsoEx },          // T2 iso at 45 min (Pec Deck / Cable Fly)
        { muscle: 'Chest', count: 1, tier: 3, phase: 2, sets: 3 },                                       // T3 at 45 min
        { muscle: 'Chest', count: 1, tier: 3, phase: 3, sets: 2 },                                       // T3 finisher at 60 min (2 sets; low-volume pump finisher)
      ]),
      // ── Back Day (5-day Bro Split paid, Aug 2026 redesign) ──
      // Standalone day — no secondary muscle.
      // Research cap (Aug 2026): 6 working exercises per session (same rationale as
      // Chest Day). Lumbar slot (2 sets, restorative) does NOT count toward the
      // working set cap — it's maintenance, not hypertrophy volume.
      // Vertical pull first (Pull-Up / Lat Pulldown) — best performed fresh.
      // Face Pull excluded from all Back slots — belongs on Shoulders/Core only.
      // Back Extension (lumbarOnly:true) excluded from all general pools by engine;
      // only the dedicated isLumbarSlot at 60 min can pick it.
      // 30 min: 1×T1 vertical + 2×T1 + 1×T2 = 12 sets → 30 min   (4 working)
      // 45 min: +1×T2 horizontal depth + 1×T3 = +6 sets → 45 min  (6 working, sweet spot)
      // 60 min: +lumbar only (2 sets) → ~60 min                    (6 working + lumbar)
      back: buildTemplate('Back Day', [
        { muscle: 'Back', count: 1, tier: 1, phase: 1, sets: 3, primaryFilter: (e) => isVerticalPullEx(e) && !/Face Pull/i.test(e.name) },          // vertical pull first (Pull-Up / Lat Pulldown)
        { muscle: 'Back', count: 2, tier: 1, phase: 1, sets: 3, primaryFilter: (e) => !/Face Pull/i.test(e.name) },                                  // T1 depth (dedup prevents repeat vertical)
        { muscle: 'Back', count: 1, tier: 2, phase: 1, sets: 3, primaryFilter: (e) => !/Face Pull/i.test(e.name) },                                  // T2 (Lat PD / Machine Row)
        { muscle: 'Back', count: 1, tier: 2, phase: 2, sets: 3, primaryFilter: (e) => isHorizontalPullEx(e) && !/Face Pull/i.test(e.name) },         // T2 horizontal depth at 45 min
        { muscle: 'Back', count: 1, tier: 3, phase: 2, sets: 3, primaryFilter: (e) => !/Face Pull/i.test(e.name) },                                  // T3 iso at 45 min (Straight-Arm PD etc.)
        { muscle: 'Back', count: 1, tier: null, phase: 3, sets: 2, isLumbarSlot: true, primaryFilter: (e) => /Back Extension/i.test(e.name) },       // lumbar only — bench owners; skips cleanly if no bench
      ]),
      legs: buildTemplate('Leg Day', [
        { muscle: 'Quads',       count: 2, tier: 1 },                           // Back Squat + Front Squat
        { muscle: 'Hamstrings', count: 1, tier: 1 },                           // Romanian Deadlift
        { muscle: 'Glutes',     count: 1, tier: 1 },                           // Hip Thrust / Bulgarian Split Squat
        { muscle: 'Quads',       count: 2, tier: 2 },                           // Goblet Squat + Leg Press
        { muscle: 'Hamstrings', count: 1, tier: 2 },                           // Stiff-Leg DL / GHR
        // T3: quad isolation first, then ham isolation, then glute finisher
        { muscle: 'Quads',       count: 1, tier: 3, primaryFilter: isQuadsEx }, // Leg Extension
        { muscle: 'Hamstrings', count: 1, tier: 3 },                           // Lying Leg Curl
        { muscle: 'Glutes',     count: 1, tier: 3 },                           // Cable Kickback / Frog Pump
      ]),
      shoulders: buildTemplate('Shoulder Day', [
        { muscle: 'Shoulders', count: 2, tier: 1 },    // OHP + Seated DB Press
        { muscle: 'Shoulders', count: 2, tier: 2 },    // Arnold Press + Machine Press
        { muscle: 'Shoulders', count: 2, tier: 3 },    // Lateral Raise + Rear Delt Fly
      ]),
      // ── Arm Day (5-day Bro Split paid) — superset-alternating pattern (Aug 2026) ──
      // Research: 2024 meta-analysis (19 studies, 313 participants) found antagonist
      // supersets produce identical hypertrophy to straight sets in significantly less
      // time. Biceps/triceps are the canonical antagonist pair — one rests while the
      // other works; intra-pair rest = just the transition time (30-45s).
      //
      // Forearms ALWAYS declared last so they render last at every session length.
      // Declaring after all bi/tri slots (regardless of their phase) means the block
      // array always terminates with forearm entries — Strength.jsx renders in block
      // order so forearms always appear after all bicep/tricep work, protecting grip.
      // Forearm sets are short (~25s/set at 15-20 reps, minimal rest) — 1 exercise ≈
      // 4 min, 2 ≈ 8 min — fits all session lengths without crowding compound work.
      //
      // 30 min: pair 1 (T1 bi ↔ T1 tri) + pair 2 (T2 bi ↔ T2 tri) × 3 sets + 1 forearm
      //          8 sets × ~2-2.5 min superset pace ≈ 16-20 min + forearm ≈ 28-32 min ✓
      // 45 min: +pair 3 (T2 depth bi ↔ T2 tri) × 3 sets + 2nd forearm ≈ 42-47 min ✓
      // 60 min: +pair 4 (T3 bi ↔ T3 tri) × 2 sets + 3rd forearm ≈ 55-62 min ✓
      arms: buildTemplate('Arm Day', [
        // ── BICEPS + TRICEPS (all session-length slots declared before forearms) ──
        // Pair 1 — T1 anchors (30 min+)
        { muscle: 'Arms', count: 1, tier: 1, phase: 1, sets: 3, primaryFilter: isBicepsEx },   // EZ Bar Curl / BB Curl / Preacher
        { muscle: 'Arms', count: 1, tier: 1, phase: 1, sets: 3, primaryFilter: isTricepsEx },  // CGBP / Rope PD / OTE
        // Pair 2 — T2 accessory (30 min+)
        { muscle: 'Arms', count: 1, tier: 2, phase: 1, sets: 3, primaryFilter: isBicepsEx },   // Hammer Curl / Incline Curl / Preacher
        { muscle: 'Arms', count: 1, tier: 2, phase: 1, sets: 3, primaryFilter: isTricepsEx },  // Skull Crusher / JM Press / Diamond PU
        // Pair 3 — T2 depth (45 min+; dedup forces distinct from pair 2)
        { muscle: 'Arms', count: 1, tier: 2, phase: 2, sets: 3, primaryFilter: isBicepsEx },   // 2nd T2 Biceps (Concentration Curl / Cable Curl)
        { muscle: 'Arms', count: 1, tier: 2, phase: 2, sets: 3, primaryFilter: isTricepsEx },  // 2nd T2 Triceps (Overhead Cable Ext / Tate Press)
        // Pair 4 — T3 finishers (60 min+)
        { muscle: 'Arms', count: 1, tier: 3, phase: 3, sets: 2, primaryFilter: isBicepsEx },   // T3 Biceps (Bayesian / Cable Concentration)
        { muscle: 'Arms', count: 1, tier: 3, phase: 3, sets: 2, primaryFilter: isTricepsEx },  // T3 Triceps (Overhead Ext / Tate Press)
        // ── FOREARMS (declared last → always renders after all bi/tri at every session length) ──
        // muscle:'Forearms' routes directly to the Forearms section of exercises.js (Wrist Curl,
        // Reverse Wrist Curl, Plate Pinch etc.). Previously 'Arms' + isForearmsEx incorrectly
        // picked Hammer Curl (primaryMuscles:['biceps','forearms']) because the true forearm
        // isolation exercises live in the separate Forearms muscle category, not Arms.
        { muscle: 'Forearms', count: 1, tier: null, phase: 1, sets: 2 },  // forearm isolaton 1 — 30 min+
        { muscle: 'Forearms', count: 1, tier: null, phase: 2, sets: 2 },  // forearm isolation 2 — 45 min+
        { muscle: 'Forearms', count: 1, tier: null, phase: 3, sets: 2 },  // forearm isolation 3 — 60 min+
      ]),
      'arms-core': buildTemplate('Arms + Core', [
        { muscle: 'Arms', count: 1, tier: 1, primaryFilter: isBicepsEx },
        { muscle: 'Arms', count: 1, tier: 1, primaryFilter: isTricepsEx },
        { muscle: 'Core', count: 1, tier: 1 },
        { muscle: 'Arms', count: 1, tier: 2, primaryFilter: isBicepsEx },
        { muscle: 'Arms', count: 1, tier: 2, primaryFilter: isTricepsEx },
        { muscle: 'Core', count: 2, tier: 2 },
        { muscle: 'Core', count: 1, tier: 3 },
      ]),
      quads: buildTemplate('Quad Day', [
        { muscle: 'Quads', count: 2, tier: 1, primaryFilter: isQuadsEx },                    // Back Squat + Front Squat
        { muscle: 'Quads', count: 2, tier: 2, primaryFilter: isQuadsEx },                    // Goblet Squat + Leg Press
        { muscle: 'Quads', count: 1, tier: 3, primaryFilter: (e) => isQuadsEx(e) && !/Jump|Plyo/i.test(e.name) }, // Leg Extension (not Box Jump)
      ]),
      postchain: buildTemplate('Posterior Chain', [
        { muscle: 'Hamstrings', count: 1, tier: 1 },    // Romanian Deadlift
        { muscle: 'Glutes',     count: 1, tier: 1 },    // Hip Thrust / Bulgarian Split Squat
        { muscle: 'Back',       count: 1, tier: 1, primaryFilter: (e) => e.primaryMuscles?.includes('back') },
        { muscle: 'Hamstrings', count: 2, tier: 2 },    // Good Morning + GHR
        { muscle: 'Glutes',     count: 2, tier: 2 },    // Single-Leg Hip Thrust + Step-Up
        { muscle: 'Hamstrings', count: 1, tier: 3 },    // Lying Leg Curl
        { muscle: 'Glutes',     count: 1, tier: 3 },    // Cable Kickback / Banded Clamshell
      ]),
      // ── 4-Day Bro Split — paid dynamic templates (Aug 2026 redesign) ──
      // Structure: 3 primary T1 + 1 secondary T1 = 12 sets × 2.5 min = 30 min
      //            +1 primary T2 + 1 secondary T2 = +6 sets → 45 min
      //            +1 primary T2 depth + 1 secondary T3 = +5 sets → ~60 min
      // All primary-muscle slots declared FIRST so Strength.jsx renders them before secondary.
      //
      // CGBP (Close-Grip Bench Press, id 107): T1 Triceps compound — appears naturally in the
      // Arms T1 phase-1 slot alongside Rope Pushdown and OTE. The engine's weekly seed
      // rotates which T1 Triceps exercise leads; CGBP will cycle in without pinning.
      // It is intentionally NOT in the Chest depth slot (requires chest/shoulder
      // stabilization best reserved for when those aren't already spent).
      'chest-tri': buildTemplate('Chest + Triceps', [
        // ── CHEST (all phases declared first — renders before Triceps at every length) ──
        // count:3 mirrors the proven Bro Split full Chest Day pattern: engine picks the 3 best
        // available T1 Chest exercises for the user's experience level and equipment profile.
        // Angle diversity (flat + incline + depth) emerges naturally via dedup + seed rotation.
        // Avoids narrow single-exercise slots that break under beginner/limited-equipment profiles.
        { muscle: 'Chest', count: 3, tier: 1, phase: 1, sets: 3 },
        { muscle: 'Chest', count: 1, tier: 2, phase: 2, sets: 3, primaryFilter: isChestIsoEx },  // Pec Deck / Cable Fly / DB Fly
        { muscle: 'Chest', count: 1, tier: 2, phase: 3, sets: 3, primaryFilter: isChestIsoEx },  // 2nd iso variation (dedup forces distinct)
        // ── TRICEPS (all phases after all Chest) ──
        { muscle: 'Arms', count: 1, tier: 1, phase: 1, sets: 3, primaryFilter: isTricepsEx },                                            // CGBP / Rope PD / OTE (seed rotates)
        { muscle: 'Arms', count: 1, tier: 2, phase: 2, sets: 3, primaryFilter: (e) => isTricepsEx(e) && !/Bench/i.test(e.name) },        // Skull Crusher / Kickback (no CGBP — chest spent)
        { muscle: 'Arms', count: 1, tier: 3, phase: 3, sets: 2, primaryFilter: (e) => isTricepsEx(e) && !/Bench/i.test(e.name) },        // T3 finisher (Diamond PU / Cable Overhead Ext)
      ]),
      'back-bi': buildTemplate('Back + Biceps', [
        // ── BACK (all phases declared first — renders before Biceps at every length) ──
        // No shoulder exercises here — Face Pull and all raises belong on Shoulders/Core Day.
        // Pull-Up/Lat Pulldown goes FIRST: these are best done fresh before the back fatigues.
        // US military uses Pull-Ups as the primary fitness benchmark for exactly this reason —
        // full T1 compound that demands peak neuromuscular output. Slot 1 always reserves this.
        { muscle: 'Back', count: 1, tier: 1, phase: 1, sets: 3, primaryFilter: isVerticalPullEx }, // Pull-Up / Lat Pulldown (FIRST)
        { muscle: 'Back', count: 2, tier: 1, phase: 1, sets: 3 },                                  // horizontal + depth T1 (dedup prevents repeating vertical)
        { muscle: 'Back', count: 1, tier: 2, phase: 2, sets: 3 },                                     // T2 (Straight-Arm PD / Machine Row)
        { muscle: 'Back', count: 1, tier: 3, phase: 3, sets: 3,
          primaryFilter: (e) => !/Face Pull/i.test(e.name) },                                          // T3 back isolation; Face Pull lives on Shoulders/Core only
        // Lumbar maintenance slot (60 min only, 2 sets): Back Extension only. isLumbarSlot:true
        // unlocks lumbarOnly exercises for this slot specifically (they are excluded from every
        // other pool). Superman is intentionally NOT a fallback here — paid users with gym access
        // have no progressive overload path on Superman (bodyweight, no load option). If the user
        // doesn't own a back-extension-bench this slot simply yields nothing; the workout is
        // slightly shorter and no rehab-style movement is silently substituted.
        { muscle: 'Back', count: 1, tier: null, phase: 3, sets: 2, isLumbarSlot: true,
          primaryFilter: (e) => /Back Extension/i.test(e.name) },                                       // lumbar maintenance — bench owners only; skips cleanly if no bench
        // ── BICEPS (all phases after all Back) ──
        { muscle: 'Arms', count: 1, tier: 1, phase: 1, sets: 3, primaryFilter: isBicepsEx },          // EZ Bar Curl / BB Curl / Preacher
        { muscle: 'Arms', count: 1, tier: 2, phase: 2, sets: 3, primaryFilter: isBicepsEx },          // Hammer Curl / Incline Curl
        { muscle: 'Arms', count: 1, tier: 3, phase: 3, sets: 2, primaryFilter: isBicepsEx },          // Cable Curl / Concentration Curl
      ]),
      // Shoulders/Core timing targets (founder spec Aug 2026):
      //   30 min: 3 shoulder exercises × 3 sets (22.5 min) + 1 core × 3 sets (7.5 min) = 30 min
      //   45 min: +1 shoulder T2 depth + 1 core functional = +6 sets → 45 min
      //   60 min: +1 shoulder trap/finisher + 1 core T2 depth = +6 sets → 60 min
      // ALL Shoulder slots declared before ALL Core slots so Strength.jsx renders them in order.
      'shoulders-core': buildTemplate('Shoulders + Core', [
        // ── SHOULDERS (all session lengths declared first) ──
        // Slot 1: T1 press always anchors first — unambiguously the highest-value compound.
        // Slots 2-4: combined isolation pool (isShoulderIsolationEx = lateral + rear delt, no shrugs).
        //   Slot 2 and Slot 3 both draw from the same pool; weekly seed determines which
        //   exercise (and which deltoid head) appears first. Dedup prevents the same exercise
        //   repeating; different seed weeks cycle the ORDER (one week lateral leads, next week
        //   rear delt leads). This is intentional progressive overload: varying which head fatigues
        //   first produces different adaptation signals — same principle the founder called out.
        //   Slot 4 (45-min depth) adds a third isolation from whatever remains in the pool.
        // Slot 5 (60-min): trap finisher — Shrug is its own separate stimulus, not in the
        //   isolation pool because it primarily targets traps rather than delt isolation.
        { muscle: 'Shoulders', count: 1, tier: 1,    phase: 1, sets: 3, primaryFilter: isShoulderPressEx },    // T1 press
        { muscle: 'Shoulders', count: 1, tier: null, phase: 1, sets: 3, primaryFilter: isShoulderIsolationEx },// isolation 1 (lateral or rear delt, seed-varied)
        { muscle: 'Shoulders', count: 1, tier: null, phase: 1, sets: 3, primaryFilter: isShoulderIsolationEx },// isolation 2 (dedup picks other head; order rotates weekly)
        { muscle: 'Shoulders', count: 1, tier: null, phase: 2, sets: 3, primaryFilter: isShoulderIsolationEx },// isolation 3 (45-min depth, any remaining isolation)
        { muscle: 'Shoulders', count: 1, tier: null, phase: 3, sets: 3,
          primaryFilter: (e) => /Shrug/i.test(e.name) },                                                       // trap finisher (60-min)
        // ── CORE (all phases after all Shoulders) ──
        { muscle: 'Core', count: 1, tier: 1,    phase: 1, sets: 3, primaryFilter: isCoreExForDay },   // Core T1 compound
        { muscle: 'Core', count: 1, tier: null, phase: 2, sets: 3, primaryFilter: isCoreExForDay },   // Core functional (any tier not yet used)
        { muscle: 'Core', count: 1, tier: 2,    phase: 3, sets: 3, primaryFilter: isCoreExForDay },   // Core T2 depth (Ab Wheel / Hanging Leg Raise)
      ]),
      // ── Bro Split 4-Day Free Static Templates (Aug 2026) ──
      // Mirrors the paid structure: 3 primary T1 × 3 sets + 1 secondary T1 × 3 sets = 30 min
      //                             +1 primary T2 + 1 secondary T2 = 45 min
      //                             +1 primary T2 depth + 1 secondary finisher = ~60 min
      // ALL primary-muscle slots declared FIRST so Strength.jsx renders them before secondary.
      // Timing check (125s avg/set):
      //   30 min: 4 ex × 3 sets = 12 sets × 125s = 25 min + 2.5 min warm-up ≈ 27.5 min ✓
      //   45 min: +2 ex × 3 sets = +6 sets → 18 × 125s + 2.5 = 45 min ✓
      //   60 min: +1 ex × 3 + 1 ex × 2 = +5 sets → 23 × 125s + 2.5 ≈ 51 min (T3 sets run
      //           ~105s each → 57–60 min with cool-down breath) ✓
      //
      // CHEST/TRICEPS — all IDs pre-existing baseline; zero new additions.
      'free-chest-tri': buildTemplate('Chest + Triceps', [
        // ── CHEST (all phases) ──
        { muscle: 'Chest', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 9   },  // Machine Chest Press (flat anchor)
        { muscle: 'Chest', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 3   },  // Incline DB Press (incline anchor)
        { muscle: 'Chest', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 8   },  // Bar Dips (depth anchor, int/adv)
        { muscle: 'Chest', count: 1, tier: null, phase: 2, sets: 3, exerciseId: 10  },  // Pec Deck (chest iso, 45min)
        { muscle: 'Chest', count: 1, tier: null, phase: 3, sets: 3, exerciseId: 4   },  // Flat DB Press (T2 depth, 60min)
        // ── TRICEPS (all phases after all Chest) ──
        { muscle: 'Arms',  count: 1, tier: null, phase: 1, sets: 3, exerciseId: 106 },  // Tricep Rope Pushdown (T1, 30min)
        { muscle: 'Arms',  count: 1, tier: null, phase: 2, sets: 3, exerciseId: 109 },  // OTE (T2, 45min)
        { muscle: 'Arms',  count: 1, tier: null, phase: 3, sets: 2, exerciseId: 110 },  // Skull Crusher (finisher, 60min — pure iso, no chest stabilization)
      ]),
      // BACK/BICEPS — Back Extension (175) added to baseline Aug 2026 for the 60-min lumbar slot.
      // Slot structure (founder direction Aug 2026):
      //   Slot 1: Lat Pulldown (37)       — universal vertical anchor, all experience levels
      //   Slot 2: Seated Cable Row (53)   — universal horizontal anchor (CGPU is in paid dynamic rotation)
      //   Slot 3: SA DB Row (47)          — horizontal depth anchor, home-gym friendly
      //   Slot 4 (45 min): Straight-Arm PD (39) — lat isolation
      //   Slot 5 (60 min): Back Extension (175)  — exerciseId path bypasses pool → lumbarOnly flag
      //           doesn't gate this slot. Superman (373) is the automatic fallback when bench is not owned.
      //           Superman is appropriate for free users (minimal-equipment context); paid uses a different
      //           lumbar slot that skips cleanly rather than substituting Superman (Aug 2026 update).
      'free-back-bi': buildTemplate('Back + Biceps', [
        // ── BACK (all session lengths declared first — renders before Biceps) ──
        { muscle: 'Back', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 37  },  // Lat Pulldown (slot 1 — universal vertical anchor, all levels)
        { muscle: 'Back', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 53  },  // Seated Cable Row (slot 2 — universal horizontal anchor; CGPU is in paid dynamic rotation)
        { muscle: 'Back', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 47  },  // SA DB Row (slot 3 — depth anchor)
        { muscle: 'Back', count: 1, tier: null, phase: 2, sets: 3, exerciseId: 39  },  // Straight-Arm Pulldown (45 min)
        { muscle: 'Back', count: 1, tier: null, phase: 3, sets: 2,                     // Back Extension (60 min — lumbar maintenance)
          exerciseId: 175, primaryFilter: (e) => /Superman/i.test(e.name) },            //   → Superman fallback if no back-extension-bench
        // ── BICEPS (all session lengths after all Back) ──
        { muscle: 'Arms', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 103 },  // DB Curl (T1, 30min)
        { muscle: 'Arms', count: 1, tier: null, phase: 2, sets: 3, exerciseId: 104 },  // Hammer Curl (T2, 45min)
        { muscle: 'Arms', count: 1, tier: null, phase: 3, sets: 2, exerciseId: 105 },  // Cable Curl (finisher, 60min)
      ]),
      // SHOULDERS/CORE — DB Shrug (371) added to baseline Aug 2026 for trap slot.
      // ALL Shoulder slots declared before ALL Core slots → Strength.jsx renders them in order.
      // Timing check:
      //   30 min: 3 Shoulder × 3 + 1 Core × 3 = 12 sets × 125s = 25 min + warm-up ≈ 30 min ✓
      //   45 min: +1 Shoulder × 3 + 1 Core × 3 = +6 sets → 18 × 125s ≈ 37.5 min + warm-up ≈ 45 min ✓
      //   60 min: +1 Shoulder × 3 + 1 Core × 3 = +6 sets → 24 × 125s = 50 min + warm-up ≈ 60 min ✓
      'free-shoulders-core': buildTemplate('Shoulders + Core', [
        // ── SHOULDERS (all phases declared first) ──
        // Tier order: T1 exercises appear at all session lengths (slots 1-3), T2 adds at 45 min.
        // Rear Delt Fly (T1) BEFORE Face Pull (T2) — T1 must not render behind T2.
        { muscle: 'Shoulders', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 76  },  // Machine Shoulder Press (T1 pressing — slot 1)
        { muscle: 'Shoulders', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 71  },  // Lateral Raise (T2 lateral — slot 2)
        { muscle: 'Shoulders', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 74  },  // Rear Delt Fly (T1 rear delt — slot 3; present at 30/45/60 min)
        { muscle: 'Shoulders', count: 1, tier: null, phase: 2, sets: 3, exerciseId: 40  },  // Face Pull (T2 rear delt — 45-min depth-add; fly + cable pull = distinct planes)
        { muscle: 'Shoulders', count: 1, tier: null, phase: 3, sets: 3, exerciseId: 371 },  // DB Shrug (trap finisher, 60min)
        // ── CORE (all phases after all Shoulders) ──
        { muscle: 'Core', count: 1, tier: null, phase: 1, sets: 3, exerciseId: 195 },  // Plank (T1, isometric)
        { muscle: 'Core', count: 1, tier: null, phase: 2, sets: 3, exerciseId: 208 },  // Suitcase Carry (T1 functional, 45min)
        { muscle: 'Core', count: 1, tier: null, phase: 3, sets: 3, exerciseId: 389 },  // Floor Leg Raise (T2 depth, 60min)
      ]),
      // Bro Split bodyweight-only templates removed (Aug 2026 — founder decision):
      // Same rationale as PPL+C: research does not support single-muscle-group days for
      // bodyweight-only or band-only users. No-equipment and band-only users on Bro Split
      // are redirected in Strength.jsx to Calisthenics Circuit, Full Body, or Upper/Lower.
    },
    // ── Bro Split rotations (Aug 2026 redesign) ──
    //
    // PAID rotations use the standalone muscle-group day templates (chest, back,
    // shoulders-core, arms, legs) built for the 5-day split. These templates have full
    // session-length gating (30/45/60 min) and dynamic engine selection.
    //
    // FREE rotations always use the 4-day synergist templates (chest-tri, back-bi,
    // shoulders-core, legs) — the getTemplate BRO_4DAY_FREE_MAP remaps those keys to
    // free static variants (free-chest-tri etc.) for free users transparently.
    // Extra days on free 5/6/7-day selections are endurance + rest, not a 5th
    // strength template (research doesn't support single-muscle-group days for
    // minimal-library users; founder direction Aug 2026).
    //
    // No overtraining advocacy: the app ships a maximum of 5 strength days/week.
    // Users who select 6 or 7 days fill extra slots with Endurance only. Two rest
    // days are included through 5-day; 6-day has 1; 7-day has none — the user is
    // explicitly choosing that density and the app surfaces it honestly.

    // 4-day paid + all free: synergist split
    fourDayRotation: ['chest-tri', 'back-bi', 'rest', 'shoulders-core', 'legs', 'rest', 'rest'],

    // 5-day paid: standalone muscle-group days (Aug 2026)
    // Chest · Back · Shoulders+Core · Arms · Legs · Rest · Rest
    defaultRotation: ['chest', 'back', 'shoulders-core', 'arms', 'legs', 'rest', 'rest'],

    // 6-day paid: 5 strength + 1 endurance + 1 rest
    sixDayRotation: ['chest', 'back', 'shoulders-core', 'arms', 'legs', 'endurance', 'rest'],

    // 7-day paid: 5 strength + 2 endurance (user's choice; no rest day app-imposed)
    sevenDayRotation: ['chest', 'back', 'shoulders-core', 'arms', 'legs', 'endurance', 'endurance'],

    // Free 5-day: mirrors the 4-day pattern (W·W·E·W·W·R·R) — mid-week break
    // replaced by endurance since the user chose 5 active days. Pairs remain split.
    fiveDayFreeRotation: ['chest-tri', 'back-bi', 'endurance', 'shoulders-core', 'legs', 'rest', 'rest'],

    // Free 6-day: mid-week endurance separates pairs; 2nd endurance replaces Sat rest
    sixDayFreeRotation: ['chest-tri', 'back-bi', 'endurance', 'shoulders-core', 'legs', 'endurance', 'rest'],

    // Free 7-day: mid-week endurance separates pairs; endurance fills both Sat + Sun
    sevenDayFreeRotation: ['chest-tri', 'back-bi', 'endurance', 'shoulders-core', 'legs', 'endurance', 'endurance'],
  },

  calisthenics: {
    id: 'calisthenics',
    name: 'Calisthenics',
    description: 'Bodyweight-only training. No gym, no problem.',
    bestFor: 'Anyone with no equipment',
    minDays: 1,
    // circuit: true → Strength page renders these sessions as timed rounds, not
    // individual set rows. circuitRounds maps tierLimit (1=30min, 2=45min, 3=60min)
    // to the number of rounds to run through all exercises in the session.
    circuit: true,
    circuitRounds: { 1: 2, 2: 3, 3: 3 },
    templates: {
      // Paid dynamic template: same muscle-group formula as Full Body (founder direction
      // Jul 2026 — bodyweight sessions should hit the same groups in the same phase order).
      //   Phase 1 (30min, 2 rounds × 5): Quads → Back → Chest → Shoulders → Core
      //   Phase 2 (45min, 3 rounds × 8): + Glutes/hinge (RDL sub) → Quads depth → Core variety
      //   Phase 3 (60min, 3 rounds × 10): + Arms/triceps depth → Core finisher
      full: buildTemplate('Bodyweight Full Body', [
        { muscle: 'Quads',     count: 1, tier: null, phase: 1 },  // BW Squat / Pistol / Shrimp Squat
        { muscle: 'Back',      count: 1, tier: null, phase: 1 },  // Door Row / Inverted Row / Pull-Up
        { muscle: 'Chest',     count: 1, tier: null, phase: 1 },  // Push-Up / Knee Push-Up
        { muscle: 'Shoulders', count: 1, tier: null, phase: 1 },  // Pike PU / Elevated Pike PU / Prone Y
        { muscle: 'Core',      count: 1, tier: null, phase: 1 },  // Plank
        { muscle: 'Glutes',    count: 1, tier: null, phase: 2 },  // Glute Bridge / SL Glute Bridge (RDL sub)
        { muscle: 'Back',      count: 1, tier: null, phase: 2 },  // Superman / Inverted Row (second pull volume)
        { muscle: 'Quads',     count: 1, tier: null, phase: 2 },  // Walking Lunge / ATG Split Squat
        { muscle: 'Core',      count: 1, tier: null, phase: 2 },  // Side Plank / Russian Twist / Shoulder Tap
        { muscle: 'Arms',      count: 1, tier: null, phase: 3 },  // Bench Dips / Bar Dips (triceps depth)
        { muscle: 'Core',      count: 1, tier: null, phase: 3 },  // Mountain Climbers / Burpees (finisher)
      ]),

      // Free-tier pinned template (static bodyweight circuit, Jul 2026 rebuild):
      // SAME muscle-group structure as the paid full template — identical group order,
      // exercises swapped to bodyweight-only and beginner-accessible pinned IDs.
      //   Phase 1 (30min, 2 rounds × 5): BW Squat, Doorframe Row, Push-Up, Prone Y Raise, Plank
      //   Phase 2 (45min, 3 rounds × 8): + Glute Bridge (hinge sub), Walking Lunge, Side Plank
      //   Phase 3 (60min, 3 rounds × 10): + Bench Dips (triceps), Mountain Climbers (finisher)
      // Free users can swap pinned exercises within the BASELINE library for variety.
      // Pike Push-Up variants (88, 387) are in BASELINE as upgrade swap options for
      // the pinned Prone Y Raise Shoulders slot when the user is ready to progress.
      'free-calisthenics': buildTemplate('Bodyweight Circuit', [
        { muscle: 'Quads',     count: 1, tier: null, phase: 1, exerciseId: 378 },  // Bodyweight Squat
        { muscle: 'Back',      count: 1, tier: null, phase: 1, exerciseId: 372 },  // Doorframe/Towel Row
        { muscle: 'Chest',     count: 1, tier: null, phase: 1, exerciseId: 7   },  // Push-Up
        { muscle: 'Shoulders', count: 1, tier: null, phase: 1, exerciseId: 386 },  // Prone Y Raise (beginner anchor)
        { muscle: 'Core',      count: 1, tier: null, phase: 1, exerciseId: 195 },  // Plank
        { muscle: 'Glutes',    count: 1, tier: null, phase: 2, exerciseId: 179 },  // Glute Bridge (RDL/hinge sub)
        { muscle: 'Back',      count: 1, tier: null, phase: 2, exerciseId: 373 },  // Superman (second pull volume)
        { muscle: 'Quads',     count: 1, tier: null, phase: 2, exerciseId: 129 },  // Walking Lunge
        { muscle: 'Core',      count: 1, tier: null, phase: 2, exerciseId: 202 },  // Side Plank
        { muscle: 'Arms',      count: 1, tier: null, phase: 3, exerciseId: 108 },  // Bench Dips (triceps depth)
        { muscle: 'Core',      count: 1, tier: null, phase: 3, exerciseId: 214 },  // Mountain Climbers (finisher)
      ]),
    },
    defaultRotation: ['full', 'rest', 'full', 'rest', 'full', 'rest', 'rest'],
  },
};

export const getProgram = (id) => programs[id] || programs['upper-lower'];

// Build a fresh (shuffled) template for a given program + template key + date string.
// The seed is deterministic: same program + same key + same date = same exercises.
// Different date = fresh selection from the full eligible exercise pool.
// Dashboard uses the static templates directly (no date seed needed for a preview card).
// Strength.jsx calls this in a useMemo keyed on the date string to get daily variety.
export const getTemplate = (programId, templateKey, dateStr = null, opts = {}) => {
  const p = getProgram(programId);

  // Free-tier static routing (Option B, Jul 2026): free users on the Full Body program
  // always get the pinned static templates (free-a / free-b) rather than the seeded
  // dynamic templates (full-a / full-b). seed=0 so the exerciseId pins resolve without
  // shuffling; the pinned templates share no exercises by design so no cross-day dedup needed.
  if (programId === 'full-body' && opts.baselineOnly) {
    const freeKey = templateKey === 'full-b' ? 'free-b' : 'free-a';
    const ft = p.templates[freeKey];
    if (ft && typeof ft.rebuild === 'function') return ft.rebuild(0, null, opts);
  }
  // Free-tier static routing for PPL+C (Aug 2026): free users always get the pinned
  // static templates instead of the seeded dynamic ones. Pull, Legs, and Core free
  // templates are built one day at a time; any not-yet-built day falls through to the
  // dynamic template (ft null-check below ensures graceful degradation).
  if (programId === 'ppl' && opts.baselineOnly && templateKey !== 'rest' && templateKey !== 'recovery') {
    const PPL_FREE_MAP = { push: 'free-push', pull: 'free-pull', legs: 'free-legs', core: 'free-core' };
    const freeKey = PPL_FREE_MAP[templateKey];
    if (freeKey) {
      const ft = p.templates[freeKey];
      if (ft && typeof ft.rebuild === 'function') return ft.rebuild(0, null, opts);
    }
  }
  // Free-tier static routing for Bro Split 4-day (Aug 2026): free users on the 4-day
  // rotation get the pinned static templates instead of the seeded dynamic ones.
  // Only the three synergist-day keys are mapped; 'legs' falls through to the dynamic
  // bro legs template filtered to baseline (acceptable until a free-legs variant is added).
  if (programId === 'bro' && opts.baselineOnly && templateKey !== 'rest' && templateKey !== 'recovery') {
    const BRO_4DAY_FREE_MAP = {
      'chest-tri':      'free-chest-tri',
      'back-bi':        'free-back-bi',
      'shoulders-core': 'free-shoulders-core',
    };
    const freeKey = BRO_4DAY_FREE_MAP[templateKey];
    if (freeKey) {
      const ft = p.templates[freeKey];
      if (ft && typeof ft.rebuild === 'function') return ft.rebuild(0, null, opts);
    }
  }
  // Free-tier calisthenics routing (Jul 2026): free users always get the pinned
  // bodyweight circuit template rather than the paid dynamic template.
  if (programId === 'calisthenics' && opts.baselineOnly && templateKey !== 'rest' && templateKey !== 'recovery') {
    const ft = p.templates['free-calisthenics'];
    if (ft && typeof ft.rebuild === 'function') return ft.rebuild(0, null, opts);
  }

  // Bodyweight routing for split programs (Aug 2026):
  // Upper/Lower is the only split program with dedicated bodyweight templates.
  // PPL+C and Bro Split no longer have bodyweight variants — users on those programs
  // with isBodyweightSetup get a redirect card in Strength.jsx pointing to
  // Calisthenics Circuit, Full Body, or Upper/Lower (all research-backed for this profile).
  if (opts.baselineOnly && opts.noEquipment && templateKey !== 'rest' && templateKey !== 'recovery') {
    const BW_TEMPLATE_MAP = {
      'upper-lower': { upper: 'free-upper', lower: 'free-lower' },
    };
    const map = BW_TEMPLATE_MAP[programId];
    const freeKey = map?.[templateKey];
    if (freeKey) {
      const ft = p.templates[freeKey];
      if (ft && typeof ft.rebuild === 'function') return ft.rebuild(0, null, opts);
    }
  }

  const t = p.templates[templateKey];
  if (!t) return null;
  if (!dateStr) return t; // no date → return static module-load template (Dashboard)
  // Some templates are plain objects with no rebuild (e.g. calisthenics' flat
  // bodyweight list). Return them as-is rather than crashing on t.rebuild().
  if (typeof t.rebuild !== 'function') return t;
  const seed = strHash(dateStr + programId + templateKey);

  // Full Body B must exclude what Fresh Full Body A picks on the same date so the two
  // sessions don't overlap. Rebuild A with the same seed, collect its names, then pass
  // that set as overrideInitialUsed to B's rebuild. Equipment/tier/combatives opts flow into both.
  if (programId === 'full-body' && templateKey === 'full-b') {
    const aSeed = strHash(dateStr + programId + 'full-a');
    const freshA = p.templates['full-a'].rebuild(aSeed, null, opts);
    const aUsed = new Set(
      freshA.blocks.map((b) => exercises.find((e) => e.id === b.refId)?.name).filter(Boolean)
    );
    return t.rebuild(seed, aUsed, opts);
  }

  return t.rebuild(seed, null, opts);
};

// ----- Schedule generation -----

// Day index Mon=0..Sun=6
export const todayIdx = () => (new Date().getDay() + 6) % 7;

// Parse "YYYY-MM-DD" as a local-time Date object (avoids UTC timezone shift bug)
export const parseLocalISO = (iso) => {
  if (!iso) return new Date();
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

// Format a Date as "YYYY-MM-DD" in local time (NOT UTC — that's what toISOString does and it's wrong here).
// Defaults to "today" when called with no args, matching parseLocalISO's behavior.
export const localISO = (d = new Date()) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Days elapsed between two ISO-date strings, treating both as local dates.
const daysBetween = (fromIso, toIso) => {
  const a = parseLocalISO(fromIso);
  const b = parseLocalISO(toIso);
  return Math.floor((b - a) / (1000 * 60 * 60 * 24));
};

// For a given (startDate, programId, experience, daysPerWeek), compute the 7-day Mon→Sun schedule for the week containing `viewDate`.
// Returns array of template keys (one per Mon..Sun) where 'rest' or 'recovery' fill off-days.
// hasRecovery: if true, 'rest' slots in the rotation become 'recovery' slots.
// opts.baselineOnly: true = free-tier user; selects free rotation variants for Bro Split
// 5/6/7-day where paid and free differ (free always uses 4-day templates + endurance fill,
// paid uses standalone muscle-group day templates).
export const getRotationFor = (programId, experience, daysPerWeek, opts = {}) => {
  const p = getProgram(programId);
  const { baselineOnly = false } = opts;

  if (p.id === 'bro') {
    const days = Number(daysPerWeek);
    // 4-day or fewer: fourDayRotation for everyone (same templates, free/paid distinction
    // happens in getTemplate via BRO_4DAY_FREE_MAP, not here).
    if (days <= 4 && p.fourDayRotation) return { rotation: p.fourDayRotation, length: 7 };

    // 5/6/7-day: paid and free diverge.
    if (baselineOnly) {
      // Free users: always the 4-day synergist template set + endurance/rest fill.
      if (days === 5 && p.fiveDayFreeRotation) return { rotation: p.fiveDayFreeRotation, length: 7 };
      if (days === 6 && p.sixDayFreeRotation)  return { rotation: p.sixDayFreeRotation,  length: 7 };
      if (days >= 7 && p.sevenDayFreeRotation) return { rotation: p.sevenDayFreeRotation, length: 7 };
    } else {
      // Paid users: standalone muscle-group day templates for 5-day.
      // defaultRotation IS the 5-day paid rotation; handled by the fall-through below.
      if (days === 6 && p.sixDayRotation)   return { rotation: p.sixDayRotation,   length: 7 };
      if (days >= 7 && p.sevenDayRotation)  return { rotation: p.sevenDayRotation,  length: 7 };
    }
  }

  return { rotation: p.defaultRotation, length: p.defaultRotation.length };
};

// Resolve the slot for a single calendar date given the user's program + start date.
// Week-to-week alternation supported via swapMap: keys swap on odd weeks.
const FULL_BODY_SWAP = { 'full-a': 'full-b', 'full-b': 'full-a' };

// baselineOnly: threads free-tier flag into getRotationFor so Bro Split 5/6/7-day
// returns free rotation variants (4-day templates + endurance fill) vs paid standalone
// day templates. Defaults false so all existing callers that don't pass it are unaffected.
const slotForDate = (date, startIso, programId, experience, daysPerWeek, baselineOnly = false) => {
  const { rotation } = getRotationFor(programId, experience, daysPerWeek, { baselineOnly });
  const diff = daysBetween(startIso, date);
  if (diff < 0) return 'rest'; // pre-start dates rest
  const idxInRotation = ((diff % rotation.length) + rotation.length) % rotation.length;
  let slot = rotation[idxInRotation];
  // Full Body: alternate A/B every full pattern cycle (= every week, since pattern is 7 days)
  if (programId === 'full-body') {
    const weekNum = Math.floor(diff / rotation.length);
    if (weekNum % 2 === 1 && FULL_BODY_SWAP[slot]) slot = FULL_BODY_SWAP[slot];
  }
  return slot;
};

// Build the 7-day Mon→Sun schedule for the week containing `viewDate`.
// `startDate` is the user's chosen Day 1 (ISO date string).
// `baselineOnly`: pass true for free-tier users so Bro Split 5/6/7-day
// returns the correct free rotation (4-day templates + endurance fill).
export const getScheduleForWeek = ({ programId, experience, daysPerWeek, startDate, hasRecovery, viewDate, baselineOnly = false }) => {
  const view = viewDate ? new Date(viewDate) : new Date();
  view.setHours(0, 0, 0, 0);
  const monday = new Date(view);
  monday.setDate(view.getDate() - ((view.getDay() + 6) % 7)); // back up to Monday
  const startIso = startDate || localISO(new Date());

  const out = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    const dayIso = localISO(day);
    out.push(slotForDate(dayIso, startIso, programId, experience, daysPerWeek, baselineOnly));
  }
  // Promote 'rest' → 'recovery' when the user has recovery activities selected,
  // BUT always preserve at least ONE true Rest day per week (founder direction
  // Jul 2 2026). Without this, any user with a recovery activity never sees a real
  // Rest day — every off-day becomes Endurance, and users don't realize true rest
  // is even scheduled. Keep the LAST rest slot of the Mon→Sun week as true Rest and
  // promote the earlier rest slots. Covers the 6-day-lifting split too: its single
  // trailing rest stays Rest instead of becoming Endurance. (Manual schedule
  // overrides bypass this entirely — an explicit user choice always wins.)
  if (hasRecovery) {
    const keepRestIdx = out.lastIndexOf('rest');
    for (let i = 0; i < out.length; i++) {
      if (out[i] === 'rest' && i !== keepRestIdx) out[i] = 'recovery';
    }
  }
  return out;
};

// Back-compat: legacy callers expect a simple schedule built from program defaults.
// Returns Mon→Sun rotation for the current week, anchored to "today" as start.
export const getDefaultSchedule = (programId, daysPerWeek, hasRecovery = false, experience = null, startDate = null) => {
  return getScheduleForWeek({
    programId,
    experience,
    daysPerWeek,
    startDate: startDate || new Date().toISOString().slice(0, 10),
    hasRecovery,
    viewDate: new Date(),
  });
};

// Day of week labels (Mon-first)
export const DAYS = [
  { idx: 0, short: 'Mon', long: 'Monday' },
  { idx: 1, short: 'Tue', long: 'Tuesday' },
  { idx: 2, short: 'Wed', long: 'Wednesday' },
  { idx: 3, short: 'Thu', long: 'Thursday' },
  { idx: 4, short: 'Fri', long: 'Friday' },
  { idx: 5, short: 'Sat', long: 'Saturday' },
  { idx: 6, short: 'Sun', long: 'Sunday' },
];


// ---------- Workout hero imagery ----------
// Maps a workout template's `title` to the on-brand AI-generated hero image
// shown on the Dashboard "Today's Workout" card.
// Add or remap as new templates ship.
const WORKOUT_HERO_BY_TITLE = {
  // PPL split
  'Push Day': '/generated/strength-chest.png',
  'Pull Day': '/generated/strength-back.png',
  'Leg Day': '/generated/strength-legs.png',
  'Core Day': '/generated/strength-core.png',

  // Upper / Lower split
  'Upper Body': '/generated/strength-upper.png',
  'Lower Body': '/generated/strength-lower.png',

  // Full Body
  'Full Body A': '/generated/strength-fullbody.png',
  'Full Body B': '/generated/strength-fullbody.png',
  'Bodyweight Full Body': '/generated/strength-fullbody.png',

  // Bro Split
  'Chest Day': '/generated/strength-chest.png',
  'Back Day': '/generated/strength-back.png',
  'Shoulder Day': '/generated/strength-shoulders.png',
  'Arm Day': '/generated/strength-arms.png',
  'Arms + Core': '/generated/strength-arms.png',
  'Quad Day': '/generated/strength-quads.png',
  'Posterior Chain': '/generated/strength-posterior.png',
};

/** Returns a hero image path for a workout template title.
 *  Falls back to the generic strength image when the title is unmapped. */
export const getWorkoutHeroImage = (title) =>
  WORKOUT_HERO_BY_TITLE[title] || '/generated/hero-strength.png';
